import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Menu, Plus, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Trash2, Calendar, User, Mail, Phone, Users, Briefcase, DollarSign, SlidersHorizontal, MapPin, Clock, AlertTriangle, CheckCircle2, PauseCircle, Loader2, FileText, ClipboardCheck, ShoppingCart, Paperclip, StickyNote, Check, Upload, ShieldCheck, Receipt, Tag, ExternalLink, PoundSterling, Car, TrendingUp, TrendingDown, History, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { Sidebar } from '../components/Sidebar';
import { useSettingsStore } from '../data/settingsStore';
import { useScreenInit } from '../useScreenInit';
// --- Types & Mock Data ---
type JobStatus = 'quote' | 'pcc_in_progress' | 'ongoing' | 'closed' | 'lost';
// Forward workflow order (Lost sits outside the linear flow)
const STATUS_FLOW: JobStatus[] = ['quote', 'pcc_in_progress', 'ongoing', 'closed'];
type CreditCheckStatus = 'not_started' | 'pending' | 'passed' | 'failed';
type PccChecklistItem = {
  id: string;
  label: string;
  done: boolean;
};
type PccInfo = {
  checklist: PccChecklistItem[];
  creditCheckStatus: CreditCheckStatus;
  creditCheckRef?: string;
  creditCheckScore?: string;
  creditCheckDate?: string;
  creditCheckNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  complete: boolean;
};
type PurchaseOrder = {
  id: string;
  number: string;
  description?: string;
  amount: number;
  date: string;
  fileName?: string;
  fileUrl?: string;
};
type JobNote = {
  id: string;
  body: string;
  author: string;
  createdAt: string;
};
type Attachment = {
  id: string;
  name: string;
  size: string;
  uploadedAt: string;
};
type TimesheetEntry = {
  id: string;
  person: string;
  date: string;
  hours: number;
  department: string;
  hourlyCost?: number;
  notes?: string;
};
type MileageEntry = {
  id: string;
  person: string;
  date: string;
  miles: number;
  department: string;
  notes?: string;
};
const EXPENSE_CATEGORIES = ['Travel', 'Accommodation', 'Meals', 'Materials', 'Equipment', 'Subcontractor', 'Software', 'Other'] as const;
type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
type Expense = {
  id: string;
  value: number;
  category: ExpenseCategory;
  department: string;
  description?: string;
  receiptName: string;
  receiptUrl: string;
  addedBy: string;
  date: string;
};
const INVOICE_CATEGORIES = ['Interim', 'Final', 'Credit note', 'Other'] as const;
export type InvoiceCategory = (typeof INVOICE_CATEGORIES)[number];
export type InvoicePaymentStatus = 'paid' | 'unpaid' | 'cancelled';
export type Invoice = {
  id: string;
  value: number;
  category: InvoiceCategory;
  department: string;
  paymentStatus: InvoicePaymentStatus;
  sageId?: string;
  description?: string;
  fileName?: string;
  fileUrl?: string;
  addedBy: string;
  date: string;
};
// Rates used to value the financial roll-up
const MILEAGE_RATE = 0.45; // £ per mile
const LEGACY_HOURLY_COST = 65; // Used only for historic entries without a stored rate
type JobActivity = {
  id: string;
  action: string;
  actor: string;
  occurredAt: string;
  detail?: string;
};
type Job = {
  id: string;
  ref: string;
  name: string;
  client: string;
  departments: string[];
  status: JobStatus;
  value: number;
  projectDirector: string;
  projectManager: string;
  startDate: string;
  endDate?: string;
  dateCreated: string;
  assignedTo?: string;
  description?: string;
  progress?: number;
  departmentBudgets?: Record<string, Record<string, number>>;
  validatedDepartmentBudgets?: Record<string, Record<string, boolean>>;
  // Extended job-management fields
  likelihood?: number;
  poRequired?: boolean;
  pcc?: PccInfo;
  purchaseOrders?: PurchaseOrder[];
  notes?: JobNote[];
  attachments?: Attachment[];
  timesheets?: TimesheetEntry[];
  expenses?: Expense[];
  invoices?: Invoice[];
  mileageEntries?: MileageEntry[];
  activity?: JobActivity[];
  // Financials roll-up inputs
  invoiced?: number; // total amount invoiced to the client
  // Job address
  street?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  // Referral
  fromReferral?: boolean;
  referralMethod?: string;
  referringClient?: string;
  // Parent relationship
  parentJobId?: string;
  parentJobRef?: string;
};
const REFERRAL_METHODS = ['Referred by client', 'Existing client', 'Word of mouth', 'Professional network', 'Repeat business', 'Email / newsletter', 'Website enquiry', 'Event / conference', 'Other'] as const;
const DEFAULT_PCC_CHECKLIST: {
  id: string;
  label: string;
}[] = [{
  id: 'engagement',
  label: 'Engagement letter signed'
}, {
  id: 'scope',
  label: 'Scope of work confirmed'
}, {
  id: 'fee',
  label: 'Fee agreement in place'
}, {
  id: 'conflict',
  label: 'Conflict of interest check'
}, {
  id: 'aml',
  label: 'Anti-money-laundering (AML) check'
}, {
  id: 'credit',
  label: 'Client credit check cleared'
}];
const makePcc = (overrides?: Partial<PccInfo>): PccInfo => ({
  checklist: DEFAULT_PCC_CHECKLIST.map(c => ({
    ...c,
    done: false
  })),
  creditCheckStatus: 'not_started',
  complete: false,
  ...overrides
});
const DEPARTMENTS = ['Project Management', 'Surveying', 'Design', 'Cost Consultancy', 'Building Surveying', 'Finance'] as const;
const DEFAULT_JOB_DEPARTMENTS = ['Project Management', 'Design'];
const INITIAL_JOB_DEPARTMENTS: Record<string, string[]> = {
  '1': ['Project Management', 'Surveying', 'Design'],
  '2': ['Project Management', 'Design', 'Cost Consultancy'],
  '3': ['Project Management', 'Building Surveying', 'Design'],
  '4': ['Project Management', 'Surveying', 'Cost Consultancy'],
  '5': ['Project Management', 'Design', 'Building Surveying'],
  '6': ['Project Management', 'Surveying', 'Design'],
  '7': ['Project Management', 'Building Surveying'],
  '8': ['Project Management', 'Surveying', 'Cost Consultancy'],
  '9': ['Project Management', 'Design'],
  '10': ['Project Management', 'Cost Consultancy', 'Finance']
};
export const formatActivityCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
function activityDateAfter(dateString: string, daysAfter: number) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + daysAfter);
  return date.toISOString();
}
function createInitialJobActivity(job: {
  id: string;
  dateCreated: string;
  projectManager: string;
  status: string;
  notes?: JobNote[];
  invoices?: Invoice[];
  expenses?: Expense[];
  mileageEntries?: MileageEntry[];
  timesheets?: TimesheetEntry[];
  purchaseOrders?: PurchaseOrder[];
}): JobActivity[] {
  const actor = job.projectManager || 'System';
  const statusActivity: JobActivity[] = job.status === 'quote' ? [] : job.status === 'lost' ? [{
    id: `activity-status-lost-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 3),
    detail: 'Quote → Lost'
  }] : [{
    id: `activity-status-pcc-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 2),
    detail: 'Quote → PCC in progress'
  }, ...(job.status === 'pcc_in_progress' ? [] : [{
    id: `activity-pcc-complete-${job.id}`,
    action: 'PCC marked complete',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 5)
  }, {
    id: `activity-status-ongoing-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 6),
    detail: 'PCC in progress → Ongoing'
  }]), ...(job.status === 'closed' ? [{
    id: `activity-status-closed-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 45),
    detail: 'Ongoing → Closed'
  }] : [])];
  const activity: JobActivity[] = [{
    id: `activity-created-${job.id}`,
    action: 'Job was created',
    actor,
    occurredAt: job.dateCreated
  }, ...statusActivity, ...(job.notes || []).map(note => ({
    id: `activity-note-${note.id}`,
    action: 'Note added',
    actor: note.author,
    occurredAt: note.createdAt,
    detail: note.body
  })), ...(job.purchaseOrders || []).map(purchaseOrder => ({
    id: `activity-po-${purchaseOrder.id}`,
    action: 'Purchase order added',
    actor,
    occurredAt: purchaseOrder.date,
    detail: `${purchaseOrder.number} · ${formatActivityCurrency(purchaseOrder.amount)}`
  })), ...(job.invoices || []).map(invoice => ({
    id: `activity-invoice-${invoice.id}`,
    action: 'Job invoice was raised',
    actor: invoice.addedBy,
    occurredAt: invoice.date,
    detail: `${invoice.category} · ${formatActivityCurrency(invoice.value)}`
  })), ...(job.expenses || []).map(expense => ({
    id: `activity-expense-${expense.id}`,
    action: 'Expense added',
    actor: expense.addedBy,
    occurredAt: expense.date,
    detail: `${expense.category} · ${formatActivityCurrency(expense.value)}`
  })), ...(job.mileageEntries || []).map(entry => ({
    id: `activity-mileage-${entry.id}`,
    action: 'Mileage added',
    actor: entry.person,
    occurredAt: entry.date,
    detail: `${entry.miles.toLocaleString('en-GB')} mi`
  })), ...(job.timesheets || []).map(entry => ({
    id: `activity-time-${entry.id}`,
    action: 'Time logged',
    actor: entry.person,
    occurredAt: entry.date,
    detail: `${entry.hours}h`
  }))];
  return activity.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}
export const INITIAL_JOBS = [{
  id: '1',
  ref: 'JOB-1041',
  name: 'Midtown Tower Refurb',
  client: 'Atlas Property Holdings',
  status: 'ongoing',
  value: 142000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Sarah Mitchell',
  startDate: '2026-03-01',
  dateCreated: '2026-02-15',
  assignedTo: 'Sarah Mitchell',
  description: 'Complete structural assessment and renovation planning for Midtown commercial tower.',
  progress: 65,
  poRequired: true,
  pcc: {
    checklist: DEFAULT_PCC_CHECKLIST.map(c => ({
      ...c,
      done: true
    })),
    creditCheckStatus: 'passed',
    creditCheckRef: 'CC-20451',
    creditCheckScore: '92',
    creditCheckDate: '2026-02-18',
    creditCheckNotes: 'Experian check. No adverse history.',
    approvedBy: 'Eleanor Hughes',
    approvedAt: '2026-02-20T10:00:00Z',
    complete: true
  },
  purchaseOrders: [{
    id: 'po1',
    number: 'PO-88231',
    description: 'Phase 1 structural survey',
    amount: 60000,
    date: '2026-02-22'
  }, {
    id: 'po2',
    number: 'PO-88247',
    description: 'Renovation planning package',
    amount: 82000,
    date: '2026-03-05'
  }],
  notes: [{
    id: 'jn1',
    body: 'Client confirmed phase 2 will follow on directly. Keep the survey team on standby.',
    author: 'Sarah Mitchell',
    createdAt: '2026-03-04T14:10:00Z'
  }],
  attachments: [{
    id: 'at1',
    name: 'Engagement-Letter-signed.pdf',
    size: '248 KB',
    uploadedAt: '2026-02-18T09:30:00Z'
  }, {
    id: 'at2',
    name: 'Structural-survey-scope.docx',
    size: '64 KB',
    uploadedAt: '2026-02-19T16:05:00Z'
  }],
  timesheets: [{
    id: 'ts1',
    person: 'Sarah Mitchell',
    date: '2026-03-02',
    hours: 7.5,
    notes: 'Site visit + survey prep'
  }, {
    id: 'ts2',
    person: 'Priya Patel',
    date: '2026-03-03',
    hours: 4,
    notes: 'Drawings review'
  }, {
    id: 'ts3',
    person: 'Sarah Mitchell',
    date: '2026-03-06',
    hours: 6
  }],
  expenses: [{
    id: 'ex1',
    value: 142.5,
    category: 'Travel',
    description: 'Rail fare for Midtown site visit',
    receiptName: 'rail-receipt.pdf',
    receiptUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Sarah Mitchell',
    date: '2026-03-02T18:20:00Z'
  }, {
    id: 'ex2',
    value: 38.0,
    category: 'Meals',
    receiptName: 'lunch.jpg',
    receiptUrl: 'https://images.unsplash.com/photo-1572441710534-3c2b1f0a2e3a?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Matthew Carter',
    date: '2026-03-03T13:05:00Z'
  }],
  invoices: [{
    id: 'inv1',
    value: 60000,
    category: 'Interim',
    department: 'Structural',
    paymentStatus: 'paid',
    description: 'Phase 1 structural survey — interim invoice',
    fileName: 'INV-20451.pdf',
    fileUrl: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Matthew Carter',
    date: '2026-03-10T09:30:00Z'
  }, {
    id: 'inv2',
    value: 18000,
    category: 'Interim',
    department: 'Planning',
    paymentStatus: 'unpaid',
    addedBy: 'Sarah Mitchell',
    date: '2026-04-18T09:30:00Z'
  }, {
    id: 'inv3',
    value: -2500,
    category: 'Credit note',
    department: 'Structural',
    paymentStatus: 'paid',
    addedBy: 'Matthew Carter',
    date: '2026-04-26T09:30:00Z'
  }, {
    id: 'inv4',
    value: 7500,
    category: 'Other',
    department: 'Planning',
    paymentStatus: 'cancelled',
    addedBy: 'Sarah Mitchell',
    date: '2026-05-02T09:30:00Z'
  }],
  mileageEntries: [{
    id: 'mil1',
    person: 'Sarah Mitchell',
    date: '2026-03-02',
    miles: 220,
    notes: 'Return trip to Midtown site'
  }, {
    id: 'mil2',
    person: 'Priya Patel',
    date: '2026-03-05',
    miles: 100
  }],
  invoiced: 60000
}, {
  id: '2',
  ref: 'JOB-1055',
  name: 'Sunset Heights Phase II',
  client: 'Crestview Developments',
  status: 'ongoing',
  value: 215000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Michael Chen',
  startDate: '2026-01-15',
  dateCreated: '2025-12-20',
  assignedTo: 'Michael Chen',
  description: 'Phase II development assessment for residential complex.',
  progress: 42
}, {
  id: '3',
  ref: 'JOB-1048',
  name: 'Magnificent Mile Tower',
  client: 'Vanguard Properties',
  status: 'pcc_in_progress',
  value: 198000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'David Reyes',
  startDate: '2026-02-01',
  dateCreated: '2026-01-10',
  assignedTo: 'David Reyes',
  progress: 78
}, {
  id: '4',
  ref: 'JOB-1029',
  name: 'Brooklyn Mixed-Use Survey',
  client: 'Atlas Property Holdings',
  status: 'quote',
  value: 88500,
  projectDirector: 'Marcus Bell',
  projectManager: 'Priya Patel',
  referralMethod: 'Referred by client',
  referringClient: 'Atlas Property Holdings',
  startDate: '2026-04-12',
  dateCreated: '2026-03-28',
  assignedTo: 'Priya Patel',
  progress: 35
}, {
  id: '5',
  ref: 'JOB-1020',
  name: 'Venice Boardwalk Retail',
  client: 'Crestview Developments',
  status: 'ongoing',
  value: 178000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Karen Sloane',
  startDate: '2026-03-04',
  dateCreated: '2026-02-18',
  progress: 52
}, {
  id: '6',
  ref: 'JOB-1012',
  name: 'River North Lofts',
  client: 'Beacon Real Estate',
  status: 'pcc_in_progress',
  value: 96500,
  projectDirector: 'Marcus Bell',
  projectManager: 'Lena Ortiz',
  startDate: '2026-02-18',
  dateCreated: '2026-01-30',
  progress: 88
}, {
  id: '7',
  ref: 'JOB-0987',
  name: 'Lexington Ave Façade',
  client: 'Atlas Property Holdings',
  status: 'closed',
  value: 64200,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Sarah Mitchell',
  startDate: '2025-09-08',
  endDate: '2026-01-22',
  dateCreated: '2025-08-15',
  assignedTo: 'Sarah Mitchell',
  progress: 100
}, {
  id: '8',
  ref: 'JOB-0978',
  name: 'Lincoln Park Condos',
  client: 'Beacon Real Estate',
  status: 'closed',
  value: 124000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Karen Sloane',
  startDate: '2025-08-14',
  endDate: '2026-04-10',
  dateCreated: '2025-07-22',
  progress: 100
}, {
  id: '9',
  ref: 'JOB-0995',
  name: 'Wicker Park Lofts',
  client: 'Vanguard Properties',
  status: 'lost',
  value: 72000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'David Reyes',
  startDate: '2025-10-15',
  dateCreated: '2025-09-28',
  assignedTo: 'David Reyes',
  progress: 25
}, {
  id: '10',
  ref: 'JOB-0951',
  name: 'Hudson Yards Fit-out',
  client: 'Atlas Property Holdings',
  status: 'quote',
  value: 41000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Priya Patel',
  startDate: '2025-11-02',
  dateCreated: '2025-10-12',
  progress: 18
}].map(job => {
  const departments = INITIAL_JOB_DEPARTMENTS[job.id] || DEFAULT_JOB_DEPARTMENTS;
  return {
    ...job,
    departments,
    timesheets: job.timesheets?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    expenses: job.expenses?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    mileageEntries: job.mileageEntries?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    invoices: job.invoices?.map(entry => ({
      ...entry
    })),
    activity: createInitialJobActivity(job)
  };
}) as unknown as Job[];
// Current signed-in user (would come from auth context in a real app)
const CURRENT_USER = 'Matthew Carter';
type SortField = 'ref' | 'name' | 'client' | 'departments' | 'projectDirector' | 'projectManager' | 'dateCreated' | 'value' | 'invoiced' | 'netProfit' | 'startDate' | 'status';
type SortOrder = 'asc' | 'desc';
const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
function getTimesheetCost(entry: TimesheetEntry) {
  return entry.hours * (entry.hourlyCost ?? LEGACY_HOURLY_COST);
}
function getTotalInvoiced(job: Job) {
  return (job.invoices || []).filter(invoice => invoice.paymentStatus !== 'cancelled').reduce((sum, invoice) => sum + invoice.value, 0);
}
function calculateNetProfit(job: Job) {
  const invoiced = getTotalInvoiced(job);
  const expenses = (job.expenses || []).reduce((sum, expense) => sum + expense.value, 0);
  const mileage = (job.mileageEntries || []).reduce((sum, entry) => sum + entry.miles, 0) * MILEAGE_RATE;
  const time = (job.timesheets || []).reduce((sum, entry) => sum + getTimesheetCost(entry), 0);
  return invoiced - expenses - mileage - time;
}
function getNextChildJobRef(parentRef: string, jobs: Job[]) {
  const existingRefs = new Set(jobs.map(job => job.ref));
  for (let index = 0; index < 702; index += 1) {
    let value = index;
    let suffix = '';
    do {
      suffix = String.fromCharCode(65 + value % 26) + suffix;
      value = Math.floor(value / 26) - 1;
    } while (value >= 0);
    const candidate = `${parentRef}-${suffix}`;
    if (!existingRefs.has(candidate)) return candidate;
  }
  return `${parentRef}-${Date.now()}`;
}
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
export function Jobs() {
  const screenInit = useScreenInit();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'budget'>(screenInit?.activeTab === 'budget' ? 'budget' : 'jobs');
  const [jobs, setJobs] = useState<Job[]>(INITIAL_JOBS);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState<JobStatus | 'all'>('all');
  const [showValidatedOnly, setShowValidatedOnly] = useState(false);
  const [sortField, setSortField] = useState<SortField>('ref');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAdmin] = useState(true);
  // KPI Totals
  const totals = useMemo(() => ({
    total: jobs.length,
    active: jobs.filter(j => j.status === 'ongoing').length,
    revenue: jobs.reduce((sum, j) => sum + j.value, 0)
  }), [jobs]);
  const filteredAndSortedJobs = useMemo(() => {
    let result = [...jobs];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(j => j.name.toLowerCase().includes(q) || j.ref.toLowerCase().includes(q) || j.client.toLowerCase().includes(q) || j.projectDirector.toLowerCase().includes(q) || j.projectManager.toLowerCase().includes(q));
    }
    if (filterDepartment) {
      result = result.filter(j => j.departments.includes(filterDepartment));
    }
    if (filterStatus !== 'all') {
      result = result.filter(j => j.status === filterStatus);
    }
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'ref') comparison = a.ref.localeCompare(b.ref, undefined, {
        numeric: true
      });else if (sortField === 'name') comparison = a.name.localeCompare(b.name);else if (sortField === 'client') comparison = a.client.localeCompare(b.client);else if (sortField === 'departments') comparison = a.departments.join(', ').localeCompare(b.departments.join(', '));else if (sortField === 'projectDirector') comparison = a.projectDirector.localeCompare(b.projectDirector);else if (sortField === 'projectManager') comparison = a.projectManager.localeCompare(b.projectManager);else if (sortField === 'dateCreated') comparison = new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();else if (sortField === 'value') comparison = a.value - b.value;else if (sortField === 'invoiced') comparison = getTotalInvoiced(a) - getTotalInvoiced(b);else if (sortField === 'netProfit') comparison = calculateNetProfit(a) - calculateNetProfit(b);else if (sortField === 'startDate') comparison = new Date(a.startDate).getTime() - new Date(b.startDate).getTime();else if (sortField === 'status') comparison = a.status.localeCompare(b.status);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [jobs, searchQuery, filterDepartment, filterStatus, sortField, sortOrder]);
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterDepartment, filterStatus, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedJobs.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedJobs = filteredAndSortedJobs.slice(pageStart, pageStart + pageSize);
  const showingFrom = filteredAndSortedJobs.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, filteredAndSortedJobs.length);
  const exportJobs = () => {
    downloadCsv('jobs-export.csv', [{
      label: 'Job ID',
      value: job => job.ref
    }, {
      label: 'Job title',
      value: job => job.name
    }, {
      label: 'Client',
      value: job => job.client
    }, {
      label: 'Departments',
      value: job => job.departments.join('; ')
    }, {
      label: 'Project director',
      value: job => job.projectDirector
    }, {
      label: 'Project manager',
      value: job => job.projectManager
    }, {
      label: 'Budget',
      value: job => job.value
    }, {
      label: 'Invoiced',
      value: job => getTotalInvoiced(job)
    }, {
      label: 'Net profit',
      value: job => calculateNetProfit(job)
    }, {
      label: 'Status',
      value: job => STATUS_CONFIG[job.status].label
    }, {
      label: 'Start date',
      value: job => job.startDate
    }, {
      label: 'Date created',
      value: job => job.dateCreated
    }], filteredAndSortedJobs);
  };
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const openJobDrawer = (job: Job) => {
    setSelectedJob(job);
    setIsDrawerOpen(true);
  };
  const openNewJobDrawer = () => {
    setSelectedJob(null);
    setIsDrawerOpen(true);
  };
  const handleSaveJob = (jobData: Partial<Job>) => {
    if (selectedJob) {
      setJobs(jobs.map(j => j.id === selectedJob.id ? {
        ...j,
        ...jobData
      } as Job : j));
    } else {
      const parentJob = jobs.find(job => job.id === jobData.parentJobId);
      const newJob: Job = {
        ...jobData,
        parentJobId: parentJob?.id,
        parentJobRef: parentJob?.ref,
        id: Math.random().toString(36).substr(2, 9),
        ref: parentJob ? getNextChildJobRef(parentJob.ref, jobs) : `JOB-${String(1000 + jobs.length + 1)}`,
        dateCreated: new Date().toISOString().split('T')[0]
      } as Job;
      setJobs([newJob, ...jobs]);
    }
    setIsDrawerOpen(false);
  };
  // Auto-save: persist edits to an existing job without closing the drawer
  const handleAutoSaveJob = (jobData: Partial<Job>) => {
    if (!selectedJob) return;
    setJobs(prev => prev.map(j => j.id === selectedJob.id ? {
      ...j,
      ...jobData
    } as Job : j));
  };
  const handleDeleteJob = (id: string) => {
    setJobs(jobs.filter(j => j.id !== id));
    setIsDrawerOpen(false);
  };
  return <div className="flex h-screen w-screen overflow-hidden font-sans" style={{
    background: 'linear-gradient(to bottom, #16486B 0%, #0F3D5C 45%, #082A40 100%)'
  }} data-id="element-1178" data-mp="3907pk7n5rufa">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-1179" data-mp="2z3bouo3i4cla" />

      <main className="flex-1 min-w-0 w-full p-0 md:py-3 md:pr-3 md:pl-0 overflow-hidden flex" data-id="element-1180" data-mp="2v18c7w53j1yf">
        <div className="flex-1 min-w-0 w-full bg-white md:rounded-2xl shadow-xl flex flex-col overflow-x-hidden overflow-y-auto" data-id="element-1181" data-mp="2q256e9hakrpe">
          {/* Header */}
          <div className="p-5 md:p-8 md:pb-0 border-b border-slate-100" data-id="element-1182" data-mp="15jb5b065cd9o">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden flex items-center gap-2 text-[#14466B] mb-4 hover:opacity-80 transition-opacity" aria-label="Open menu" data-id="element-1183" data-mp="1aiecaq5ostka">
              <Menu className="w-6 h-6" data-id="element-1184" data-mp="1jjlwk4bdbtvd" />
            </button>

            <div data-id="element-1185" data-mp="1jhrmjl1mxm62">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-1186" data-mp="mtlc2hi17zjv">Jobs</h1>
              <p className="text-slate-500 text-sm mt-1" data-id="element-1187" data-mp="mvfw7tlhbnmw">
                Manage active projects and assignments across all departments.
              </p>
            </div>

            {/* Tabs + inline controls */}
            <div className="flex flex-col gap-3 mt-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-1188" data-mp="vsginuoznbnp">
              <div className="flex items-center gap-6" data-id="element-1189" data-mp="10muiqaeli6yp">
                <button onClick={() => setActiveTab('jobs')} className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === 'jobs' ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1190" data-mp="34uf2mfrg70u">
                  All Jobs
                  {activeTab === 'jobs' && <motion.div layoutId="jobs-active-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#14466B]" data-id="element-1191" data-mp="16hb7eef20nzf" />}
                </button>
                <button onClick={() => setActiveTab('budget')} className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === 'budget' ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1192" data-mp="1ka0ukm6bjyy4">
                  Monthly Budget Allocations
                  {activeTab === 'budget' && <motion.div layoutId="jobs-active-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#14466B]" data-id="element-1193" data-mp="nk0ah0gwa6n5" />}
                </button>
              </div>

              {/* Search + Filters — inline with tabs */}
              <div className="flex items-center gap-3 pb-3" data-id="element-1194" data-mp="2cwbvxz1r23vz">
                <div className="relative flex-1 lg:flex-none lg:w-56" data-id="element-1195" data-mp="2cyomh0lkpmkn">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1196" data-mp="27wpgdiyg8bk0" />
                  <input type="text" placeholder="Search jobs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 placeholder:text-slate-400" data-id="element-1197" data-mp="2gdq1oh4urd43" />
                </div>

                <FilterPopover filterDepartment={filterDepartment} setFilterDepartment={setFilterDepartment} filterStatus={filterStatus} setFilterStatus={setFilterStatus} showValidatedOnly={showValidatedOnly} setShowValidatedOnly={setShowValidatedOnly} sortField={sortField} sortOrder={sortOrder} onSortChange={(field, order) => {
                setSortField(field);
                setSortOrder(order);
              }} data-id="element-1198" data-mp="2z593ikn54l4j" />

                {activeTab === 'jobs' && <button type="button" onClick={exportJobs} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export filtered jobs as CSV" data-id="element-1199" data-mp="3i2aauc462xbn"><Download className="h-4 w-4" data-id="element-1200" data-mp="1hzix76s8pear" /><span className="hidden xl:inline" data-id="element-1201" data-mp="1d29aw005dir5">Export CSV</span></button>}
                {activeTab === 'jobs' && <button onClick={openNewJobDrawer} className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] text-white text-sm font-medium transition-colors whitespace-nowrap shadow-sm" data-id="element-1202" data-mp="19176z5vaciiu">
                    <Plus className="w-4 h-4" data-id="element-1203" data-mp="20teqce26e90x" />
                    <span className="hidden sm:inline" data-id="element-1204" data-mp="1wqi144i4reo0">New job</span>
                  </button>}
              </div>
            </div>
          </div>

          {activeTab === 'jobs' ? <>
              {/* Table Area */}
              <div className="flex-1 flex flex-col px-5 md:px-8 pt-5 md:pt-6 pb-8 md:pb-10 min-h-0" data-id="element-1205" data-mp="lbw7rstl2w6t">
                {filteredAndSortedJobs.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1206" data-mp="gaozztrfxx05">
                    <Briefcase className="w-12 h-12 text-slate-300 mb-4" data-id="element-1207" data-mp="2wxx0aywjrsjr" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-1208" data-mp="310juh6goxpk4">
                      No jobs found
                    </h3>
                    <p className="text-slate-500 text-sm max-w-sm" data-id="element-1209" data-mp="35rtik2mlcktw">
                      We couldn't find any jobs matching your current filters.
                      Try adjusting your search or adding a new job.
                    </p>
                    <button onClick={() => {
                setSearchQuery('');
                setFilterDepartment('');
                setFilterStatus('all');
              }} className="mt-4 text-[#14466B] text-sm font-medium hover:underline" data-id="element-1210" data-mp="3fr0viopeydgh">
                      Clear filters
                    </button>
                  </div> : <>
                    {/* Desktop Table */}
                    <div className="hidden md:block flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl" data-id="element-1211" data-mp="1uc4ut91ijhs1">
                      <table className="w-full text-left border-collapse" data-id="element-1212" data-mp="1fvts59qtr0xy">
                        <thead className="sticky top-0 z-10" data-id="element-1213" data-mp="1kpobgc2ipdwi">
                          <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1214" data-mp="16xroemfphu6z">
                            <SortableHeader label="Job ID" field="ref" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-28" data-id="element-1215" data-mp="1ay226l9qpowe" />
                            <SortableHeader label="Job Title" field="name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1216" data-mp="1yoxnr0nlrnv7" />
                            <SortableHeader label="Departments" field="departments" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1217" data-mp="1pnq1jgyd2dhh" />
                            <SortableHeader label="Project Director" field="projectDirector" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1218" data-mp="1ukzsx25b8z7y" />
                            <SortableHeader label="Project Manager" field="projectManager" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1219" data-mp="j7xcc737o9ns" />
                            <SortableHeader label="Budget" field="value" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1220" data-mp="asgepkksmlfi" />
                            <SortableHeader label="Invoiced" field="invoiced" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1221" data-mp="kni66n2jjzl9" />
                            <SortableHeader label="Net profit" field="netProfit" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1222" data-mp="3o59t3n1tltcl" />
                            <SortableHeader label="Status" field="status" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-44" data-id="element-1223" data-mp="3j804uaq63nqc" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-1224" data-mp="3t2jorkfsz18v">
                          {paginatedJobs.map(job => <tr key={job.id} onClick={() => openJobDrawer(job)} className="hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-1225" data-mp="35cpg7f2inyuf">
                              <td className="py-3 px-4 text-sm text-slate-500 font-medium" data-id="element-1226" data-mp="14bhqnd9i07bs">
                                {job.ref}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 font-medium group-hover:text-[#14466B] transition-colors" data-id="element-1227" data-mp="1e2m58bv9hq63">
                                {job.name}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1228" data-mp="le83pm4ysbps">
                                <div className="flex flex-wrap gap-1" data-id="element-1229" data-mp="qcrqfordtrj7">
                                  {job.departments.map(department => <span key={department} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700" data-id="element-1230" data-mp="uemsivh508y1">
                                      {department}
                                    </span>)}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1231" data-mp="2arbkrvjf26ow">
                                {job.projectDirector}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1232" data-mp="2fwyp321666v6">
                                {job.projectManager}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 font-semibold tabular-nums" data-id="element-1233" data-mp="2b4dxm75qk5ut">
                                {formatCurrency(job.value)}
                              </td>
                              <td className="py-3 px-4 text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1234" data-mp="223pjzdk987ig">
                                {formatCurrency(getTotalInvoiced(job))}
                              </td>
                              <td className={`py-3 px-4 text-sm font-semibold tabular-nums ${calculateNetProfit(job) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-id="element-1235" data-mp="1o6kdjcgmp6mo">
                                {calculateNetProfit(job) < 0 ? `−${formatCurrency(Math.abs(calculateNetProfit(job)))}` : formatCurrency(calculateNetProfit(job))}
                              </td>
                              <td className="py-3 px-4" data-id="element-1236" data-mp="3pobby0l5yu83">
                                <StatusBadge status={job.status} data-id="element-1237" data-mp="3umv3qhmfv08d" />
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3" data-id="element-1238" data-mp="3kq9wrqquzf1w">
                      {paginatedJobs.map(job => <div key={job.id} onClick={() => openJobDrawer(job)} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm active:bg-slate-50 transition-colors" data-id="element-1239" data-mp="36uxp6irltu3k">
                          <div className="flex justify-between items-start mb-3" data-id="element-1240" data-mp="2x0fhl2j2heeh">
                            <h3 className="font-semibold text-slate-900 text-sm" data-id="element-1241" data-mp="31ypfvm038v4b">
                              {job.name}
                            </h3>
                            <span className="text-xs font-medium text-slate-400" data-id="element-1242" data-mp="2swzpmjhy0284">
                              {job.ref}
                            </span>
                          </div>
                          <div className="mb-3 flex items-center gap-2 flex-wrap" data-id="element-1243" data-mp="29daiw3f3ug91">
                            <StatusBadge status={job.status} data-id="element-1244" data-mp="24eh84ir7d3ck" />
                            {job.departments.map(department => <span key={department} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700" data-id="element-1245" data-mp="2ia1cm966pj0g">
                                {department}
                              </span>)}
                          </div>
                          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100" data-id="element-1246" data-mp="2nape5os1hi8x">
                            <div data-id="element-1247" data-mp="31zlltkq6ot90">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1248" data-mp="2x11xjdo4b5dh">
                                Project Director
                              </div>
                              <div className="text-sm font-medium text-slate-900" data-id="element-1249" data-mp="3aygtz2csgdey">
                                {job.projectDirector}
                              </div>
                            </div>
                            <div data-id="element-1250" data-mp="28neumeznht98">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1251" data-mp="28oghdgq1446s">
                                Project Manager
                              </div>
                              <div className="text-sm font-medium text-slate-900" data-id="element-1252" data-mp="2hfyufiroi3vn">
                                {job.projectManager}
                              </div>
                            </div>
                            <div data-id="element-1253" data-mp="2rctu44zouabi">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1254" data-mp="2rc20gzpnimnv">
                                Budget
                              </div>
                              <div className="text-sm font-semibold text-slate-900" data-id="element-1255" data-mp="30e1hhs0o1qqt">
                                {formatCurrency(job.value)}
                              </div>
                            </div>
                            <div data-id="element-1256" data-mp="162mnv04jb75s">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1257" data-mp="ygrafh8cjb39">
                                Invoiced
                              </div>
                              <div className="text-sm font-semibold text-slate-900" data-id="element-1258" data-mp="or7qe88wdb6t">
                                {formatCurrency(getTotalInvoiced(job))}
                              </div>
                            </div>
                            <div data-id="element-1259" data-mp="1hg55y3lq4tvv">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1260" data-mp="1hfdcaybot688">
                                Net profit
                              </div>
                              <div className={`text-sm font-semibold ${calculateNetProfit(job) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-id="element-1261" data-mp="18dnixmy8vx7t">
                                {calculateNetProfit(job) < 0 ? `−${formatCurrency(Math.abs(calculateNetProfit(job)))}` : formatCurrency(calculateNetProfit(job))}
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    {/* Pagination */}
                    <Pagination page={safePage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} setPage={setPage} showingFrom={showingFrom} showingTo={showingTo} total={filteredAndSortedJobs.length} data-id="element-1262" data-mp="1w6d1m8nc1vmf" />
                  </>}
              </div>
            </> : <BudgetAllocationsTab jobs={filteredAndSortedJobs} onOpenJob={openJobDrawer} onUpdateBudget={(jobId, department, monthKey, amount) => {
          setJobs(prev => prev.map(j => {
            if (j.id !== jobId) return j;
            return {
              ...j,
              departmentBudgets: {
                ...(j.departmentBudgets || {}),
                [department]: {
                  ...(j.departmentBudgets?.[department] || {}),
                  [monthKey]: amount
                }
              },
              validatedDepartmentBudgets: {
                ...(j.validatedDepartmentBudgets || {}),
                [department]: {
                  ...(j.validatedDepartmentBudgets?.[department] || {}),
                  [monthKey]: false
                }
              }
            };
          }));
        }} onValidateBudget={(jobId, department, monthKey) => {
          setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            validatedDepartmentBudgets: {
              ...(j.validatedDepartmentBudgets || {}),
              [department]: {
                ...(j.validatedDepartmentBudgets?.[department] || {}),
                [monthKey]: true
              }
            }
          } : j));
        }} onUnvalidateBudget={(jobId, department, monthKey) => {
          setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            validatedDepartmentBudgets: {
              ...(j.validatedDepartmentBudgets || {}),
              [department]: {
                ...(j.validatedDepartmentBudgets?.[department] || {}),
                [monthKey]: false
              }
            }
          } : j));
        }} showValidatedOnly={showValidatedOnly} data-id="element-1263" data-mp="2at694rqm1x42" />}
        </div>
      </main>

      <AnimatePresence data-id="element-1264" data-mp="1wifzuwp7m2rv">
        {isDrawerOpen && <JobDrawer job={selectedJob} availableJobs={jobs} isAdmin={isAdmin} onClose={() => setIsDrawerOpen(false)} onSave={handleSaveJob} onAutoSave={handleAutoSaveJob} onDelete={selectedJob && isAdmin ? () => handleDeleteJob(selectedJob.id) : undefined} data-id="element-1265" data-mp="2b85cbo2r5t7g" />}
      </AnimatePresence>
    </div>;
}
// --- Status Badge ---
const STATUS_CONFIG: Record<JobStatus, {
  label: string;
  bg: string;
  text: string;
  dot: string;
}> = {
  quote: {
    label: 'Quote',
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    dot: 'bg-slate-400'
  },
  pcc_in_progress: {
    label: 'PCC In Progress',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500'
  },
  ongoing: {
    label: 'Ongoing',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500'
  },
  closed: {
    label: 'Closed',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500'
  },
  lost: {
    label: 'Lost',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-500'
  }
};
function StatusBadge({
  status
}: {
  status: JobStatus;
}) {
  const {
    bg,
    text,
    dot,
    label
  } = STATUS_CONFIG[status];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${bg} ${text}`} data-id="element-1266" data-mp="31hwbbd0so7dn">
      <span className="relative flex w-1.5 h-1.5" aria-hidden data-id="element-1267" data-mp="3u60ik2z0ksig">
        {status === 'ongoing' && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-1268" data-mp="3geve8nkpbc1s" />}
        <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${dot}`} data-id="element-1269" data-mp="3kfyuvcakpc42" />
      </span>
      {label}
    </span>;
}
// Larger, more prominent status indicator used in the job drawer header
function ProminentStatus({
  status
}: {
  status: JobStatus;
}) {
  const {
    bg,
    text,
    dot,
    label
  } = STATUS_CONFIG[status];
  return <span className={`inline-flex items-center gap-2 pl-3 pr-4 py-1.5 rounded-full text-sm font-semibold border ${bg} ${text} border-current/20`} data-id="element-1270" data-mp="qmud2x2o69sc">
      <span className="relative flex w-2.5 h-2.5" aria-hidden data-id="element-1271" data-mp="hmfq7ta24mmq">
        {status === 'ongoing' && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-1272" data-mp="cnl6mp49mu1k" />}
        <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${dot}`} data-id="element-1273" data-mp="7p1kanlrfg8o" />
      </span>
      {label}
    </span>;
}
// --- Progress Bar ---
function ProgressBar({
  progress
}: {
  progress: number;
}) {
  return <div className="flex items-center gap-2" data-id="element-1274" data-mp="3v0svshea3q0d">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-1275" data-mp="3uzhque93kc7o">
        <div className="h-full bg-[#14466B] rounded-full transition-all duration-300" style={{
        width: `${progress}%`
      }} data-id="element-1276" data-mp="3q42exrungqdw" />
      </div>
      <span className="text-xs text-slate-500 font-medium tabular-nums w-8 text-right" data-id="element-1277" data-mp="3g4lblh2veol3">
        {progress}%
      </span>
    </div>;
}
// --- Sortable Header ---
type SortableHeaderProps = {
  label: string;
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
};
function SortableHeader({
  label,
  field,
  sortField,
  sortOrder,
  onSort,
  className = ''
}: SortableHeaderProps) {
  const isActive = sortField === field;
  return <th className={`py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors group ${className}`} onClick={() => onSort(field)} aria-sort={isActive ? sortOrder === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-1278" data-mp="2naf243xd6rqy">
      <div className="flex items-center gap-1" data-id="element-1279" data-mp="2x68dvl39eh7o">
        {label}
        {isActive ? sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 text-[#14466B]" data-id="element-1280" data-mp="320m83k66qiar" /> : <ChevronDown className="w-3 h-3 text-[#14466B]" data-id="element-1281" data-mp="24im8q8edv4ip" /> : <ChevronDown className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1282" data-mp="29iidwayth1zu" />}
      </div>
    </th>;
}
// --- Filter Popover ---
type FilterPopoverProps = {
  filterDepartment: string;
  setFilterDepartment: (department: string) => void;
  filterStatus: JobStatus | 'all';
  setFilterStatus: (status: JobStatus | 'all') => void;
  showValidatedOnly: boolean;
  setShowValidatedOnly: (value: boolean) => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
};
function FilterPopover({
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus,
  showValidatedOnly,
  setShowValidatedOnly,
  sortField,
  sortOrder,
  onSortChange
}: FilterPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  const activeFilterCount = (filterDepartment ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0) + (showValidatedOnly ? 1 : 0);
  return <div className="relative" ref={popoverRef} data-id="element-1283" data-mp="1ab3slp9j3evf">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 bg-white text-sm font-medium text-slate-700 transition-colors whitespace-nowrap" data-id="element-1284" data-mp="1f9yljk17mjt9">
        <SlidersHorizontal className="w-4 h-4" data-id="element-1285" data-mp="16bd2zovqn4sh" />
        Filters
        {activeFilterCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#14466B] text-white text-xs font-semibold" data-id="element-1286" data-mp="rdt0combdlph">
            {activeFilterCount}
          </span>}
      </button>

      <AnimatePresence data-id="element-1287" data-mp="2lq0ptmfv9adv">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-id="element-1288" data-mp="2lr23lyo9oaza">
            <div className="p-4 border-b border-slate-100" data-id="element-1289" data-mp="2faaa66wy6s68">
              <h3 className="font-semibold text-slate-900 text-sm" data-id="element-1290" data-mp="2fak5ma0rhrjb">
                Filters & Sort
              </h3>
            </div>

            <div className="p-4 space-y-4 max-h-96 overflow-y-auto" data-id="element-1291" data-mp="37yy1aj4dobrp">
              {/* Department filter */}
              <div data-id="element-1292" data-mp="3304lul5j6aow">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1293" data-mp="330wa1awst655">
                  Department
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1294" data-mp="2u7j6zg50so5c">
                  <button onClick={() => setFilterDepartment('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterDepartment === '' ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1295" data-mp="2u71g6z14n24t">
                    All
                  </button>
                  {DEPARTMENTS.map(department => <button key={department} onClick={() => setFilterDepartment(department)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterDepartment === department ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1296" data-mp="18ifjpx0hw8cb">
                      {department}
                    </button>)}
                </div>
              </div>

              {/* Status Filter */}
              <div data-id="element-1297" data-mp="1wds2g3ngt33r">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1298" data-mp="1wd8loq9jwkrb">
                  Status
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1299" data-mp="2174frhmp1nh7">
                  {(['all', 'quote', 'pcc_in_progress', 'ongoing', 'closed', 'lost'] as const).map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === status ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1300" data-mp="2a6hna4r9c1za">
                      {status === 'all' ? 'All' : STATUS_CONFIG[status].label}
                    </button>)}
                </div>
              </div>

              <div data-id="element-1301" data-mp="1z0r3em6rkq4z">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1302" data-mp="1yzy7kg9cfl1m">Validation</label>
                <button type="button" onClick={() => setShowValidatedOnly(!showValidatedOnly)} aria-pressed={showValidatedOnly} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${showValidatedOnly ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1303" data-mp="17aw7u7sbc47v"><CheckCircle2 className="h-3.5 w-3.5" data-id="element-1304" data-mp="1bby5ik6l087q" />Validated allocations only</button>
              </div>

              {/* Sort */}
              <div data-id="element-1305" data-mp="nlcbhwtewogq">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1306" data-mp="nksq264jzhxy">
                  Sort By
                </label>
                <select value={`${sortField}-${sortOrder}`} onChange={e => {
              const [field, order] = e.target.value.split('-') as [SortField, SortOrder];
              onSortChange(field, order);
            }} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" data-id="element-1307" data-mp="9gbxbwjmhxp7">
                  <option value="ref-asc" data-id="element-1308" data-mp="s7bfadspa78z">Job ID (Low to High)</option>
                  <option value="ref-desc" data-id="element-1309" data-mp="x6xuutw03zky">Job ID (High to Low)</option>
                  <option value="name-asc" data-id="element-1310" data-mp="x669ebw4bi8e">Job Title (A-Z)</option>
                  <option value="name-desc" data-id="element-1311" data-mp="i8ncund780qe">Job Title (Z-A)</option>
                  <option value="projectDirector-asc" data-id="element-1312" data-mp="n8i3cpt2aoax">
                    Project Director (A-Z)
                  </option>
                  <option value="projectManager-asc" data-id="element-1313" data-mp="32zmozncxkqoo">
                    Project Manager (A-Z)
                  </option>
                  <option value="value-desc" data-id="element-1314" data-mp="380k7y25rlor8">Budget (High to Low)</option>
                  <option value="value-asc" data-id="element-1315" data-mp="25ji4gbg0dbx5">Budget (Low to High)</option>
                  <option value="netProfit-desc" data-id="element-1316" data-mp="29k21lf0l0fja">Net Profit (High to Low)</option>
                  <option value="netProfit-asc" data-id="element-1317" data-mp="29m6f8ooa8swr">Net Profit (Low to High)</option>
                  <option value="startDate-desc" data-id="element-1318" data-mp="2ek7vz2y8kg52">Start Date (Newest)</option>
                  <option value="startDate-asc" data-id="element-1319" data-mp="2jh6rnpxjsv5d">Start Date (Oldest)</option>
                  <option value="dateCreated-desc" data-id="element-1320" data-mp="1lvitazhj82r7">
                    Date Created (Newest)
                  </option>
                  <option value="dateCreated-asc" data-id="element-1321" data-mp="1qulz4m5c6d08">Date Created (Oldest)</option>
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 flex gap-2" data-id="element-1322" data-mp="1vr3zl9tmvp6r">
              <button onClick={() => {
            setFilterDepartment('');
            setFilterStatus('all');
            setShowValidatedOnly(false);
            setIsOpen(false);
          }} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-1323" data-mp="20l883xijs59y">
                Clear
              </button>
              <button onClick={() => setIsOpen(false)} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-[#14466B] text-white hover:bg-[#0F3D5C] transition-colors" data-id="element-1324" data-mp="vk7bujag9w3i">
                Apply
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Pagination ---
type PaginationProps = {
  page: number;
  totalPages: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  setPage: (page: number) => void;
  showingFrom: number;
  showingTo: number;
  total: number;
};
function Pagination({
  page,
  totalPages,
  pageSize,
  setPageSize,
  setPage,
  showingFrom,
  showingTo,
  total
}: PaginationProps) {
  return <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-100 mt-6" data-id="element-1325" data-mp="3u2cu3y7yvhxn">
      <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1326" data-mp="3q099aizer0zp">
        <span data-id="element-1327" data-mp="3ffnmf93k9yg6">Show</span>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="px-2 py-1 rounded border border-slate-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" data-id="element-1328" data-mp="3ff5swk8a9yby">
          {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size} data-id="element-1329" data-mp="36gts5pdpdk5s">
              {size}
            </option>)}
        </select>
        <span data-id="element-1330" data-mp="3bec31lba69gf">
          Showing {showingFrom}–{showingTo} of {total}
        </span>
      </div>

      <div className="flex items-center gap-2" data-id="element-1331" data-mp="2z258pefbqeg">
        <button onClick={() => setPage(page - 1)} disabled={page === 1} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Previous page" data-id="element-1332" data-mp="70dng9uyx9cm">
          <ChevronLeft className="w-4 h-4 text-slate-600" data-id="element-1333" data-mp="3ubde9nvfh5st" />
        </button>

        <div className="flex items-center gap-1" data-id="element-1334" data-mp="lw3e4lb3mano">
          {Array.from({
          length: totalPages
        }, (_, i) => i + 1).filter(p => {
          if (totalPages <= 7) return true;
          if (p === 1 || p === totalPages) return true;
          if (p >= page - 1 && p <= page + 1) return true;
          return false;
        }).map((p, idx, arr) => {
          const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
          return <Fragment key={p} data-id="element-1335" data-mp="19rojhny8f3o3" data-mpi={idx}>
                  {showEllipsis && <span className="px-2 text-slate-400 text-sm" data-id="element-1336" data-mp="14t3jnpmw6m06">...</span>}
                  <button onClick={() => setPage(p)} className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === p ? 'bg-[#14466B] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1337" data-mp="h3stf2364a0a">
                    {p}
                  </button>
                </Fragment>;
        })}
        </div>

        <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Next page" data-id="element-1338" data-mp="m2cdsy245dqj">
          <ChevronRight className="w-4 h-4 text-slate-600" data-id="element-1339" data-mp="1xhiv626tem6r" />
        </button>
      </div>
    </div>;
}
// --- Job Drawer ---
type JobDrawerProps = {
  job: Job | null;
  availableJobs: Job[];
  isAdmin: boolean;
  onClose: () => void;
  onSave: (data: Partial<Job>) => void;
  onAutoSave?: (data: Partial<Job>) => void;
  onDelete?: () => void;
};
type JobTab = 'details' | 'brief' | 'pcc' | 'pos' | 'notes' | 'financials' | 'history';
const drawerInputCls = 'w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm';
function JobDrawer({
  job,
  availableJobs,
  isAdmin,
  onClose,
  onSave,
  onAutoSave,
  onDelete
}: JobDrawerProps) {
  const isEdit = !!job;
  const [activeTab, setActiveTab] = useState<JobTab>('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const briefFileInputRef = useRef<HTMLInputElement>(null);
  // Full working draft of the job
  const [draft, setDraft] = useState<Job>(() => ({
    id: job?.id || '',
    ref: job?.ref || '',
    name: job?.name || '',
    client: job?.client || '',
    departments: job?.departments || DEFAULT_JOB_DEPARTMENTS,
    status: job?.status || 'quote',
    value: job?.value || 0,
    projectDirector: job?.projectDirector || '',
    projectManager: job?.projectManager || '',
    startDate: job?.startDate || '',
    endDate: job?.endDate || '',
    dateCreated: job?.dateCreated || new Date().toISOString().split('T')[0],
    assignedTo: job?.assignedTo || '',
    description: job?.description || '',
    progress: job?.progress || 0,
    departmentBudgets: job?.departmentBudgets,
    likelihood: job?.likelihood ?? 50,
    poRequired: job?.poRequired ?? false,
    pcc: job?.pcc || makePcc(),
    purchaseOrders: job?.purchaseOrders || [],
    notes: job?.notes || [],
    attachments: job?.attachments || [],
    timesheets: job?.timesheets || [],
    expenses: job?.expenses || [],
    invoices: job?.invoices || [],
    mileageEntries: job?.mileageEntries || [],
    activity: job?.activity || (job ? createInitialJobActivity(job) : []),
    invoiced: job?.invoiced ?? 0,
    street: job?.street || '',
    line2: job?.line2 || '',
    city: job?.city || '',
    county: job?.county || '',
    postcode: job?.postcode || '',
    fromReferral: job?.fromReferral ?? false,
    referralMethod: job?.referralMethod || '',
    referringClient: job?.referringClient || '',
    parentJobId: job?.parentJobId,
    parentJobRef: job?.parentJobRef
  }));
  const update = (patch: Partial<Job>) => {
    setDraft(d => ({
      ...d,
      ...patch
    }));
    setError(null);
  };
  const addBriefFiles = (files: FileList | null) => {
    if (!files) return;
    const attachments = Array.from(files).map(file => ({
      id: Math.random().toString(36).slice(2, 11),
      name: file.name,
      size: file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`,
      uploadedAt: new Date().toISOString()
    }));
    update({
      attachments: [...(draft.attachments || []), ...attachments]
    });
    attachments.forEach(attachment => recordActivity('File added', attachment.name));
    if (briefFileInputRef.current) briefFileInputRef.current.value = '';
  };
  const handleParentJobChange = (parentId: string) => {
    const parent = availableJobs.find(item => item.id === parentId);
    if (!parent) {
      update({
        parentJobId: undefined,
        parentJobRef: undefined
      });
      return;
    }
    update({
      parentJobId: parent.id,
      parentJobRef: parent.ref,
      name: parent.name,
      client: parent.client,
      departments: [...parent.departments],
      projectDirector: parent.projectDirector,
      projectManager: parent.projectManager,
      assignedTo: parent.assignedTo || '',
      poRequired: parent.poRequired ?? false,
      value: parent.value,
      likelihood: parent.likelihood ?? 50,
      startDate: parent.startDate,
      endDate: parent.endDate || '',
      description: parent.description || '',
      street: parent.street || '',
      line2: parent.line2 || '',
      city: parent.city || '',
      county: parent.county || '',
      postcode: parent.postcode || '',
      fromReferral: parent.fromReferral ?? false,
      referralMethod: parent.referralMethod || '',
      referringClient: parent.referringClient || ''
    });
  };
  const referringClients = useMemo(() => Array.from(new Set([...availableJobs.map(item => item.client), draft.referringClient || ''].filter(Boolean))).sort((a, b) => a.localeCompare(b)), [availableJobs, draft.referringClient]);
  const recordActivity = (action: string, detail?: string, actor: string = CURRENT_USER) => {
    const entry: JobActivity = {
      id: Math.random().toString(36).slice(2, 11),
      action,
      actor,
      occurredAt: new Date().toISOString(),
      detail
    };
    setDraft(d => ({
      ...d,
      activity: [entry, ...(d.activity || [])]
    }));
  };
  const updatePcc = (patch: Partial<PccInfo>) => setDraft(d => ({
    ...d,
    pcc: {
      ...(d.pcc || makePcc()),
      ...patch
    }
  }));
  const pcc = draft.pcc || makePcc();
  const poCount = draft.purchaseOrders?.length || 0;
  const checklistDone = pcc.checklist.every(c => c.done);
  const poRuleOk = !draft.poRequired || poCount > 0;
  const canCompletePcc = checklistDone && poRuleOk;
  const canBeOngoing = pcc.complete;
  // --- Status workflow helpers ---
  const currentFlowIndex = STATUS_FLOW.indexOf(draft.status);
  const nextStatus = currentFlowIndex >= 0 && currentFlowIndex < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentFlowIndex + 1] : null;
  const progressBlockedByPcc = nextStatus === 'ongoing' && !canBeOngoing;
  const handleStatusChange = (next: JobStatus) => {
    if (next === 'ongoing' && !canBeOngoing) {
      setError('PCC must be completed before this job can move to Ongoing. See the PCC tab.');
      return;
    }
    const previousStatus = draft.status;
    update({
      status: next
    });
    recordActivity('Job changed status', `${STATUS_CONFIG[previousStatus].label} → ${STATUS_CONFIG[next].label}`);
  };
  // Auto-save edits to an existing job (debounced) without closing the drawer
  const didMount = useRef(false);
  useEffect(() => {
    if (!isEdit || !onAutoSave) return;
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const t = setTimeout(() => onAutoSave(draft), 400);
    return () => clearTimeout(t);
  }, [draft, isEdit, onAutoSave]);
  const poTotal = (draft.purchaseOrders || []).reduce((sum, p) => sum + p.amount, 0);
  const handleSubmit = () => {
    if (!draft.name.trim()) {
      setActiveTab('details');
      setError('Job title is required.');
      return;
    }
    if (!draft.client.trim() || draft.departments.length === 0) {
      setActiveTab('details');
      setError('Client and at least one department are required.');
      return;
    }
    if (draft.status === 'ongoing' && !canBeOngoing) {
      setActiveTab('pcc');
      setError('PCC must be completed before this job can be Ongoing.');
      return;
    }
    onSave({
      ...draft,
      activity: [{
        id: Math.random().toString(36).slice(2, 11),
        action: 'Job was created',
        actor: CURRENT_USER,
        occurredAt: new Date().toISOString()
      }, ...(draft.activity || [])]
    });
  };
  const handleAddNote = () => {
    const body = newNote.trim();
    if (!body) return;
    const note: JobNote = {
      id: Math.random().toString(36).substr(2, 9),
      body,
      author: 'Matthew Carter',
      createdAt: new Date().toISOString()
    };
    update({
      notes: [note, ...(draft.notes || [])]
    });
    recordActivity('Note added', body);
    setNewNote('');
  };
  const tabs: {
    id: JobTab;
    label: string;
    icon: React.ReactNode;
    count?: number;
  }[] = [{
    id: 'details',
    label: 'Details',
    icon: <User className="w-4 h-4" data-id="element-1340" data-mp="3p0mqf2czof9m" />
  }, {
    id: 'history',
    label: 'History',
    icon: <History className="w-4 h-4" data-id="element-1341" data-mp="3kzkm4747td0s" />,
    count: draft.activity?.length || 0
  }, {
    id: 'brief',
    label: 'Brief',
    icon: <FileText className="w-4 h-4" data-id="element-1342" data-mp="2u7q8wldqptm" />
  }, {
    id: 'pcc',
    label: 'PCC',
    icon: <ClipboardCheck className="w-4 h-4" data-id="element-1343" data-mp="3u0il2azsquci" />
  }, {
    id: 'pos',
    label: 'Purchase Orders',
    icon: <ShoppingCart className="w-4 h-4" data-id="element-1344" data-mp="36aep8psunyw2" />,
    count: poCount
  }, {
    id: 'notes',
    label: 'Notes & Files',
    icon: <StickyNote className="w-4 h-4" data-id="element-1345" data-mp="31cczjm0x51bm" />,
    count: (draft.notes?.length || 0) + (draft.attachments?.length || 0)
  }, {
    id: 'financials',
    label: 'Financials',
    icon: <PoundSterling className="w-4 h-4" data-id="element-1346" data-mp="3ba15l83z5v93" />
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-end" onClick={onClose} data-id="element-1347" data-mp="3b86k9sosk14h">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'tween',
      duration: 0.28,
      ease: [0.32, 0.72, 0, 1]
    }} onClick={e => e.stopPropagation()} className="w-full sm:max-w-4xl h-full bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" data-id="element-1348" data-mp="2iizk6p9ov3h4">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-100 gap-4" data-id="element-1349" data-mp="t4rnsz888n6v">
          <div className="min-w-0" data-id="element-1350" data-mp="1guu8gu9taqvg">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-1351" data-mp="1grq1xq8vjj1m">
              <h2 className="text-xl font-bold text-slate-900 truncate" data-id="element-1352" data-mp="1ls3xw9x7b2ja">
                {isEdit ? draft.name || 'Untitled job' : 'New job'}
              </h2>
              <ProminentStatus status={draft.status} data-id="element-1353" data-mp="1crftm6xm0gjl" />
            </div>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-1354" data-mp="1ctsbkk3daqwn">
              {isEdit ? <>
                  <span className="font-medium text-slate-600" data-id="element-1355" data-mp="3sl6b46v0b9zq">
                    {draft.ref}
                  </span>
                  {' · '}
                  {draft.client || 'No client'}
                </> : 'Create a new job and capture its setup details.'}
            </p>
            {draft.departments.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Assigned departments" data-id="element-1356" data-mp="6wm4cbatihkw">
                {draft.departments.map(department => <span key={department} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700" data-id="element-1357" data-mp="2w27z9y2jhzt">
                    {department}
                  </span>)}
              </div>}
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0" aria-label="Close" data-id="element-1358" data-mp="ukdtwgldcr44">
            <X className="w-5 h-5" data-id="element-1359" data-mp="ll0ktp15q4jx" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 sm:px-6 border-b border-slate-100 overflow-x-auto" data-id="element-1360" data-mp="3bcf8db1m1rxj">
          {tabs.map(t => <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} className={`relative flex items-center gap-2 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === t.id ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1361" data-mp="36du6762ssy6g">
              {t.icon}
              {t.label}
              {typeof t.count === 'number' && t.count > 0 && <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${activeTab === t.id ? 'bg-[#14466B] text-white' : 'bg-slate-200 text-slate-700'}`} data-id="element-1362" data-mp="1zlwxbmho2yx9">
                  {t.count}
                </span>}
              {activeTab === t.id && <motion.div layoutId="job-drawer-tab" className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#14466B]" data-id="element-1363" data-mp="2ejpaw93nld8t" />}
            </button>)}
        </div>

        {/* Validation banner */}
        {error && <div className="mx-6 mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-id="element-1364" data-mp="k5ocfpc0ms99">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-1365" data-mp="o7rpgiec24wn" />
            <span data-id="element-1366" data-mp="1rhdxcium511t">{error}</span>
          </div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-id="element-1367" data-mp="1mikk8rj8nc26">
          {activeTab === 'details' && <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1368" data-mp="20hahq89yzdr2">
              {isEdit && <div data-id="element-1369" data-mp="20f7jj0ynyeb9">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1370" data-mp="1wffhl73rz6ev">
                    Job Number
                  </label>
                  <input type="text" disabled value={draft.ref} className={`${drawerInputCls} bg-slate-50 text-slate-500 cursor-not-allowed`} data-id="element-1371" data-mp="2ae5d4iay4y14" />
                </div>}
              {!isEdit && <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-id="element-1372" data-mp="2k4hw46344j55">
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="parent-job" data-id="element-1373" data-mp="2f7hr7yi01mfm">
                    Parent job <span className="text-slate-400 font-normal" data-id="element-1374" data-mp="2t6r52riqg4gf">(optional)</span>
                  </label>
                  <select id="parent-job" value={draft.parentJobId || ''} onChange={e => handleParentJobChange(e.target.value)} className={drawerInputCls} data-id="element-1375" data-mp="2o8y1854wjmjb">
                    <option value="" data-id="element-1376" data-mp="3csj6w61ky9r3">No parent job</option>
                    {[...availableJobs].sort((a, b) => a.ref.localeCompare(b.ref, undefined, {
                numeric: true
              })).map(parent => <option key={parent.id} value={parent.id} data-id="element-1377" data-mp="2yukv98en8rfa">
                        {parent.ref} · {parent.name}
                      </option>)}
                  </select>
                  {draft.parentJobRef ? <p className="mt-2 text-xs text-[#14466B]" data-id="element-1378" data-mp="2p0lykoxc9zuq">
                      New job ID: <span className="font-semibold" data-id="element-1379" data-mp="2tugf5jhndypl">{getNextChildJobRef(draft.parentJobRef, availableJobs)}</span>. Setup details have been copied and can be edited below.
                    </p> : <p className="mt-2 text-xs text-slate-500" data-id="element-1380" data-mp="2k43wxxxb2hmm">
                      Select a parent job to copy its setup details and create a suffixed job ID.
                    </p>}
                </div>}
              <div className={isEdit ? '' : 'md:col-span-2'} data-id="element-1381" data-mp="2b1ckbusp1fte">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1382" data-mp="2ql72r7h741yv">
                  Job Title <span className="text-red-500" data-id="element-1383" data-mp="2llu65v0ivgj3">*</span>
                </label>
                <input type="text" value={draft.name} onChange={e => update({
              name: e.target.value
            })} className={drawerInputCls} placeholder="Enter job title" data-id="element-1384" data-mp="2ljri3gixjxh0" />
              </div>
              <div data-id="element-1385" data-mp="27lt2y8wsbx0g">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1386" data-mp="27mbai1owdxno">
                  Client <span className="text-red-500" data-id="element-1387" data-mp="22vcqar0sgff5">*</span>
                </label>
                <input type="text" value={draft.client} onChange={e => update({
              client: e.target.value
            })} className={drawerInputCls} placeholder="Client name" data-id="element-1388" data-mp="22t9prd20f3w6" />
              </div>
              <div className="md:col-span-2" data-id="element-1389" data-mp="1ovbon9cp93ym">
                <span className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1390" data-mp="1ou0bwk1a0lvd">
                  Departments <span className="text-red-500" data-id="element-1391" data-mp="1jx9yy5krq6e6">*</span>
                </span>
                <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 p-2" data-id="element-1392" data-mp="3vk3q1pcjely6">
                  {DEPARTMENTS.map(department => {
                const selected = draft.departments.includes(department);
                return <button key={department} type="button" aria-pressed={selected} onClick={() => update({
                  departments: selected ? draft.departments.filter(d => d !== department) : [...draft.departments, department]
                })} className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${selected ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1393" data-mp="8g5xuqhklz7u">
                        {department}
                      </button>;
              })}
                </div>
                <p className="mt-1 text-xs text-slate-500" data-id="element-1394" data-mp="2t53hhtsl9pas">
                  Select every department delivering work on this job.
                </p>
              </div>
              <div data-id="element-1395" data-mp="2y2d9nh7d4f2b">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1396" data-mp="27u9dwksuem0t">
                  Project Director <span className="text-red-500" data-id="element-1397" data-mp="2ctdkr4sao7nz">*</span>
                </label>
                <input type="text" value={draft.projectDirector} onChange={e => update({
              projectDirector: e.target.value
            })} className={drawerInputCls} placeholder="Director name" data-id="element-1398" data-mp="2cr9issdyhiqc" />
              </div>
              <div data-id="element-1399" data-mp="1p41eods9kfpw">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1400" data-mp="1p3j8opfsun4s">
                  Project Manager <span className="text-red-500" data-id="element-1401" data-mp="1turj9osv8ejj">*</span>
                </label>
                <input type="text" value={draft.projectManager} onChange={e => update({
              projectManager: e.target.value
            })} className={drawerInputCls} placeholder="Manager name" data-id="element-1402" data-mp="1g3d1tmelwlel" />
              </div>
              <div data-id="element-1403" data-mp="164nj5revywxa">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1404" data-mp="165fdkywqyolz">
                  Budget (£) <span className="text-red-500" data-id="element-1405" data-mp="1b2p1290ksq76">*</span>
                </label>
                <div className="relative" data-id="element-1406" data-mp="rhf2ib2t5zdx">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none" data-id="element-1407" data-mp="rkayvuz0cg09">
                    £
                  </span>
                  <input type="text" inputMode="numeric" value={draft.value ? draft.value.toLocaleString('en-GB') : ''} onChange={e => {
                const digits = e.target.value.replace(/[^\d]/g, '');
                update({
                  value: digits ? Number(digits) : 0
                });
              }} className={`${drawerInputCls} pl-7`} placeholder="0" data-id="element-1408" data-mp="11h5vu9fmu8cf" />
                </div>
              </div>
              <div data-id="element-1409" data-mp="1k6cexxkc91yr">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1410" data-mp="1k8qb69qr8k7w">
                  Likelihood (%)
                </label>
                <div className="relative" data-id="element-1411" data-mp="1t6sj798wod4o">
                  <input type="number" min="0" max="100" value={draft.likelihood ?? 0} onChange={e => {
                const n = Number(e.target.value);
                update({
                  likelihood: Math.max(0, Math.min(100, n || 0))
                });
              }} className={`${drawerInputCls} pr-8`} placeholder="0" data-id="element-1412" data-mp="1t7ttvcm0exlv" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none" data-id="element-1413" data-mp="267ml1c5vc5sd">
                    %
                  </span>
                </div>
              </div>
              <div data-id="element-1414" data-mp="2f069xkwcyw00">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1415" data-mp="2eymv7yqcot0c">
                  Start Date
                </label>
                <input type="date" value={draft.startDate} onChange={e => update({
              startDate: e.target.value
            })} className={drawerInputCls} data-id="element-1416" data-mp="2k0uc92q1ez9j" />
              </div>
              <div data-id="element-1417" data-mp="2twfaqqvwdhej">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1418" data-mp="2tukxlyqvatl0">
                  End Date
                </label>
                <input type="date" value={draft.endDate} onChange={e => update({
              endDate: e.target.value
            })} className={drawerInputCls} data-id="element-1419" data-mp="ziebi4wfqsdx" />
              </div>
              {/* Job address */}
              <div className="md:col-span-2 pt-2" data-id="element-1420" data-mp="381egh8rpluhw">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1421" data-mp="380mijran3kog">
                  <MapPin className="w-4 h-4" data-id="element-1422" data-mp="2z212q3wjyjrd" />
                  Job Address
                </h3>
              </div>
              <div className="md:col-span-2" data-id="element-1423" data-mp="3vsbjfw1j1hh0">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1424" data-mp="3vpxnzm2xq38x">
                  Street
                </label>
                <input type="text" value={draft.street} onChange={e => update({
              street: e.target.value
            })} className={drawerInputCls} placeholder="Street address" data-id="element-1425" data-mp="3mr3ljf2xaing" />
              </div>
              <div className="md:col-span-2" data-id="element-1426" data-mp="2bcq2qon10tea">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1427" data-mp="2bdhzi2sd0x65">
                  Line 2
                </label>
                <input type="text" value={draft.line2} onChange={e => update({
              line2: e.target.value
            })} className={drawerInputCls} placeholder="Apartment, suite, unit (optional)" data-id="element-1428" data-mp="3u9tfeqcjeovb" />
              </div>
              <div data-id="element-1429" data-mp="3l9wkw83guvsc">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1430" data-mp="3laem7j4zk074">
                  City
                </label>
                <input type="text" value={draft.city} onChange={e => update({
              city: e.target.value
            })} className={drawerInputCls} placeholder="City" data-id="element-1431" data-mp="3q63ty9r5snei" />
              </div>
              <div data-id="element-1432" data-mp="lrvu7rchgacx">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1433" data-mp="7u79qkmbv58e">
                  County
                </label>
                <input type="text" value={draft.county} onChange={e => update({
              county: e.target.value
            })} className={drawerInputCls} placeholder="County" data-id="element-1434" data-mp="csih8op5h3ng" />
              </div>
              <div data-id="element-1435" data-mp="2xkkcvbjvktrm">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1436" data-mp="2xl3wqxt35s8a">
                  Postcode
                </label>
                <input type="text" value={draft.postcode} onChange={e => update({
              postcode: e.target.value
            })} className={drawerInputCls} placeholder="Postcode" data-id="element-1437" data-mp="2q1unr5ohrvjr" />
              </div>

              {/* Referral */}
              <div className="md:col-span-2" data-id="element-1438" data-mp="2u1d3zo5dbig9">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1439" data-mp="2tziia7m9vmb4">
                  Referral method
                </label>
                <select value={draft.referralMethod} onChange={e => update({
              referralMethod: e.target.value,
              referringClient: e.target.value === 'Referred by client' ? draft.referringClient : ''
            })} className={drawerInputCls} data-id="element-1440" data-mp="22ayx4bvrv6e3">
                  <option value="" data-id="element-1441" data-mp="2b9ale7961s3d">Select a method…</option>
                  {REFERRAL_METHODS.map(m => <option key={m} value={m} data-id="element-1442" data-mp="1njg8xqsre5jn">
                      {m}
                    </option>)}
                </select>
              </div>
              {draft.referralMethod === 'Referred by client' && <div className="md:col-span-2" data-id="element-1443" data-mp="3imld8xrzash0">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1444" data-mp="3nn0onjsp9pwc">Referring client</label>
                  <select value={draft.referringClient || ''} onChange={e => update({
              referringClient: e.target.value
            })} className={drawerInputCls} data-id="element-1445" data-mp="3se91kpt8nte9">
                    <option value="" data-id="element-1446" data-mp="1a0rph14art8">Select the client who referred this job…</option>
                    {referringClients.map(client => <option key={client} value={client} data-id="element-1447" data-mp="34owoheusj16o">{client}</option>)}
                  </select>
                </div>}
            </div>}

          {activeTab === 'brief' && <div className="p-6 space-y-5" data-id="element-1448" data-mp="39mpy6hvaydbp">
              <div data-id="element-1449" data-mp="3dq4df6bf2po3">
                <h3 className="text-base font-semibold text-slate-900" data-id="element-1450" data-mp="3dqnvqo4zbg2n">
                  Job brief
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-1451" data-mp="ox8v1tezjqsm">
                  A short summary of the scope and intent of this job
                </p>
              </div>
              <div data-id="element-1452" data-mp="2dho72ij3u9fw">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1453" data-mp="2dg4scwd3k6g8">
                  Brief summary
                </label>
                <textarea value={draft.description} onChange={e => update({
              description: e.target.value
            })} rows={10} className={`${drawerInputCls} resize-y`} placeholder="Brief job summary" data-id="element-1454" data-mp="29i71lp3ohcaq" />
              </div>
              <div data-id="element-1455" data-mp="1ur6qfcbjr4aw">
                <div className="mb-2 flex items-center justify-between" data-id="element-1456" data-mp="1urq2iceimyh0">
                  <label className="text-sm font-medium text-slate-700" data-id="element-1457" data-mp="1prked67gfu3o">Brief files</label>
                  <button type="button" onClick={() => briefFileInputRef.current?.click()} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#14466B] hover:underline" data-id="element-1458" data-mp="1lpqidb87fe9u">
                    <Upload className="h-3.5 w-3.5" data-id="element-1459" data-mp="3g3zrdg8hsshf" />Add file
                  </button>
                  <input ref={briefFileInputRef} type="file" multiple className="hidden" onChange={event => addBriefFiles(event.target.files)} data-id="element-1460" data-mp="3b56oehqu0jvi" />
                </div>
                {(draft.attachments || []).length === 0 ? <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-400" data-id="element-1461" data-mp="2oynrpu9y2lst">No brief files attached yet.</p> : <div className="space-y-2" data-id="element-1462" data-mp="2a8zqet1rjn53">
                    {(draft.attachments || []).map(attachment => <div key={attachment.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5" data-id="element-1463" data-mp="2f8cd9dsmwu7o">
                        <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" data-id="element-1464" data-mp="2181hpxgdqrg6" />
                        <div className="min-w-0 flex-1" data-id="element-1465" data-mp="25bpq2icu5r2h"><p className="truncate text-sm text-slate-800" data-id="element-1466" data-mp="258tuv1sdhghq">{attachment.name}</p><p className="text-xs text-slate-400" data-id="element-1467" data-mp="1rcgk0bicgdc8">{attachment.size}</p></div>
                        <button type="button" onClick={() => update({
                  attachments: (draft.attachments || []).filter(item => item.id !== attachment.id)
                })} className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Remove ${attachment.name}`} data-id="element-1468" data-mp="1wbb21b8hlxva"><X className="h-4 w-4" data-id="element-1469" data-mp="1f0nwnk78nhhu" /></button>
                      </div>)}
                  </div>}
              </div>
            </div>}

          {activeTab === 'pcc' && <div className="p-6 space-y-6" data-id="element-1470" data-mp="19zq90s3glv8y">
              {/* PCC status banner */}
              <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${pcc.complete ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-1471" data-mp="1os1snhzzdz68">
                {pcc.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" data-id="element-1472" data-mp="1xpu9iqlcpevq" /> : <ClipboardCheck className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-1473" data-mp="1ssu50k45gk6q" />}
                <div className="min-w-0" data-id="element-1474" data-mp="27pl4suhqjxwv">
                  <p className="text-sm font-medium text-slate-900" data-id="element-1475" data-mp="27ojuis8jnfg7">
                    {pcc.complete ? 'PCC complete' : 'PCC in progress'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-1476" data-mp="dbagcn51z5rm">
                    {pcc.complete ? `Approved by ${pcc.approvedBy || 'admin'}${pcc.approvedAt ? ` on ${new Date(pcc.approvedAt).toLocaleDateString('en-GB')}` : ''}.` : 'Complete all checklist items, the credit check, and any required purchase orders to finish PCC.'}
                  </p>
                </div>
              </div>

              {/* Checklist */}
              <div data-id="element-1477" data-mp="3j94swjzcrnfn">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1478" data-mp="3j9ob81sx0du7">
                  Checklist
                </h3>
                <div className="space-y-1.5" data-id="element-1479" data-mp="3t72knhgf82d0">
                  {pcc.checklist.map(item => {
                const isCredit = item.id === 'credit';
                // The credit-check item can only be ticked once a score and
                // date checked have both been recorded.
                const creditReady = !!(pcc.creditCheckScore || '').trim() && !!(pcc.creditCheckDate || '').trim();
                const toggleBlocked = isCredit && !item.done && !creditReady;
                return <div key={item.id} className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-1480" data-mp="27vj81b0j07zx">
                        <button type="button" disabled={toggleBlocked} onClick={() => updatePcc({
                    checklist: pcc.checklist.map(c => c.id === item.id ? {
                      ...c,
                      done: !c.done
                    } : c)
                  })} className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left ${toggleBlocked ? 'cursor-not-allowed' : 'hover:bg-slate-50'}`} title={toggleBlocked ? 'Enter a credit score and date checked before marking this complete' : undefined} data-id="element-1481" data-mp="23uh2ydjxh1q1">
                          <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${item.done ? 'bg-emerald-500 text-white' : `border-2 ${toggleBlocked ? 'border-slate-200' : 'border-slate-300'}`}`} data-id="element-1482" data-mp="1ezjoe8l778t1">
                            {item.done && <Check className="w-3.5 h-3.5" data-id="element-1483" data-mp="2vdjopknyh0gt" />}
                          </span>
                          <span className={`text-sm ${item.done ? 'text-slate-500 line-through' : toggleBlocked ? 'text-slate-400' : 'text-slate-700'}`} data-id="element-1484" data-mp="2vbp467gljad9">
                            {item.label}
                          </span>
                          {isCredit && <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-slate-400" data-id="element-1485" data-mp="2tw4g683y6enp">
                              <ShieldCheck className="w-3.5 h-3.5" data-id="element-1486" data-mp="2oy2pb10a5b1o" />
                              Details
                            </span>}
                        </button>

                        {/* Credit check details live within the credit tick box */}
                        {isCredit && <div className="border-t border-slate-200 bg-slate-50/60 px-3 py-3 space-y-3" data-id="element-1487" data-mp="3cmc8dv63zkp4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1488" data-mp="37vdluduj1qdf">
                              <div data-id="element-1489" data-mp="32xkirtoitchd">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1490" data-mp="32w9bhjvv9mli">
                                  Score
                                </label>
                                <input type="text" inputMode="numeric" value={pcc.creditCheckScore || ''} onChange={e => updatePcc({
                          creditCheckScore: e.target.value
                        })} className={drawerInputCls} placeholder="e.g. 92" data-id="element-1491" data-mp="3vlgfgye3b4e5" />
                              </div>
                              <div data-id="element-1492" data-mp="gompws4xmqp5">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1493" data-mp="grr2odwgqurf">
                                  Date checked
                                </label>
                                <input type="date" value={pcc.creditCheckDate || ''} onChange={e => updatePcc({
                          creditCheckDate: e.target.value
                        })} className={drawerInputCls} data-id="element-1494" data-mp="ppt8d6r56bl1" />
                              </div>
                              <div className="sm:col-span-2" data-id="element-1495" data-mp="14fidte69p1r4">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1496" data-mp="14h38r4whumpi">
                                  Notes
                                </label>
                                <textarea value={pcc.creditCheckNotes || ''} onChange={e => updatePcc({
                          creditCheckNotes: e.target.value
                        })} rows={2} className={`${drawerInputCls} resize-none`} placeholder="Credit agency, any conditions..." data-id="element-1497" data-mp="1dg6s89zkp44j" />
                              </div>
                            </div>
                            {toggleBlocked && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-id="element-1498" data-mp="2fwtdle87tq68">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1499" data-mp="1s6qpf97f94cw" />
                                Enter a credit score and date checked to mark
                                this item complete.
                              </p>}
                          </div>}
                      </div>;
              })}
                </div>
              </div>

              {/* Approval / completion */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1500" data-mp="3ckhp3ddrukgt">
                {pcc.complete && pcc.approvedBy && <p className="text-sm text-slate-600 flex items-center gap-1.5" data-id="element-1501" data-mp="37mg58q8iuh4a">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-1502" data-mp="3g25bgsg14ohy" />
                    Approved by{' '}
                    <span className="font-medium text-slate-900" data-id="element-1503" data-mp="3k6mtam2twrq3">
                      {pcc.approvedBy}
                    </span>
                    {pcc.approvedAt ? ` on ${new Date(pcc.approvedAt).toLocaleDateString('en-GB')}` : ''}
                  </p>}
                {!poRuleOk && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-id="element-1504" data-mp="2pv77kiev0oc">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-1505" data-mp="7o6gnu4sn961" />
                    This client requires a purchase order. Add at least one on
                    the Purchase Orders tab before completing PCC.
                  </p>}
                {!checklistDone && <p className="text-xs text-slate-500" data-id="element-1506" data-mp="3u1n1fb52a7sw">
                    Complete every checklist item to enable PCC sign-off.
                  </p>}
                {pcc.complete ? <button type="button" onClick={() => updatePcc({
              complete: false,
              approvedAt: undefined,
              approvedBy: undefined
            })} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-1507" data-mp="qfg0aw8a3ebc">
                    Reopen PCC
                  </button> : <button type="button" disabled={!canCompletePcc} onClick={() => {
              updatePcc({
                complete: true,
                approvedAt: new Date().toISOString(),
                approvedBy: CURRENT_USER
              });
              recordActivity('PCC marked complete');
            }} className="px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center gap-2" data-id="element-1508" data-mp="malxyuk8sjha">
                    <CheckCircle2 className="w-4 h-4" data-id="element-1509" data-mp="3uq5kzbubl5nw" />
                    Mark PCC complete
                  </button>}
              </div>
            </div>}

          {activeTab === 'pos' && <PurchaseOrdersPanel poRequired={!!draft.poRequired} setPoRequired={v => update({
          poRequired: v
        })} orders={draft.purchaseOrders || []} total={poTotal} onAdd={po => {
          update({
            purchaseOrders: [...(draft.purchaseOrders || []), po]
          });
          recordActivity('Purchase order added', `${po.number} · ${formatCurrency(po.amount)}`);
        }} onRemove={id => update({
          purchaseOrders: (draft.purchaseOrders || []).filter(p => p.id !== id)
        })} data-id="element-1510" data-mp="3ptore24cpz3f" />}

          {activeTab === 'notes' && <JobNotesPanel notes={draft.notes || []} attachments={draft.attachments || []} newNote={newNote} setNewNote={setNewNote} onAddNote={handleAddNote} onAddAttachment={a => {
          update({
            attachments: [...(draft.attachments || []), a]
          });
          recordActivity('File added', a.name);
        }} onRemoveAttachment={id => update({
          attachments: (draft.attachments || []).filter(a => a.id !== id)
        })} data-id="element-1511" data-mp="1qlf7q03twlj3" />}

          {activeTab === 'financials' && <FinancialsPanel value={draft.value || 0} departments={draft.departments} currentUser={CURRENT_USER} isAdmin={isAdmin} mileageEntries={draft.mileageEntries || []} onAddMileage={m => {
          update({
            mileageEntries: [m, ...(draft.mileageEntries || [])]
          });
          recordActivity('Mileage added', `${m.miles.toLocaleString('en-GB')} mi`, m.person);
        }} onRemoveMileage={id => update({
          mileageEntries: (draft.mileageEntries || []).filter(m => m.id !== id)
        })} timesheets={draft.timesheets || []} onAddTimesheet={ts => {
          update({
            timesheets: [ts, ...(draft.timesheets || [])]
          });
          recordActivity('Time logged', `${ts.hours}h`, ts.person);
        }} onRemoveTimesheet={id => update({
          timesheets: (draft.timesheets || []).filter(t => t.id !== id)
        })} expenses={draft.expenses || []} onAddExpense={exp => {
          update({
            expenses: [exp, ...(draft.expenses || [])]
          });
          recordActivity('Expense added', `${exp.category} · ${formatCurrency(exp.value)}`, exp.addedBy);
        }} onRemoveExpense={id => update({
          expenses: (draft.expenses || []).filter(e => e.id !== id)
        })} invoices={draft.invoices || []} onAddInvoice={inv => {
          update({
            invoices: [inv, ...(draft.invoices || [])]
          });
          recordActivity('Job invoice was raised', `${inv.category} · ${formatCurrency(inv.value)}`, inv.addedBy);
        }} onRemoveInvoice={id => update({
          invoices: (draft.invoices || []).filter(i => i.id !== id)
        })} data-id="element-1512" data-mp="15s9ssiuev6d" />}

          {activeTab === 'history' && <JobHistoryPanel activity={draft.activity || []} data-id="element-1513" data-mp="os46t52pdu0j" />}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3" data-id="element-1514" data-mp="tqp877toyjqk">
          {isEdit ? <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-1515" data-mp="fvmwu562s26d">
              <button type="button" onClick={() => handleStatusChange('lost')} disabled={draft.status === 'lost'} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${draft.status === 'lost' ? 'border-slate-200 text-slate-400 cursor-not-allowed' : 'border-rose-200 text-rose-700 hover:bg-rose-50'}`} data-id="element-1516" data-mp="ku6i03cuh9xo">
                <X className="w-4 h-4" data-id="element-1517" data-mp="3hx2lfvxk19n5" />
                {draft.status === 'lost' ? 'Marked as Lost' : 'Mark as Lost'}
              </button>
              <button type="button" onClick={() => nextStatus && handleStatusChange(nextStatus)} disabled={!nextStatus || progressBlockedByPcc} title={progressBlockedByPcc ? 'Complete PCC before moving to Ongoing' : undefined} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${nextStatus && !progressBlockedByPcc ? 'bg-[#14466B] hover:bg-[#0F3D5C] text-white shadow-sm' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-1518" data-mp="3qw62yvh2pgzj">
                {progressBlockedByPcc ? <ShieldCheck className="w-4 h-4" data-id="element-1519" data-mp="1yxcvg61rco9n" /> : <ChevronRight className="w-4 h-4" data-id="element-1520" data-mp="1yyefv14nyn41" />}
                {nextStatus ? progressBlockedByPcc ? 'Complete PCC to progress' : `Progress to ${STATUS_CONFIG[nextStatus].label}` : 'No further status'}
              </button>
            </div> : <div className="flex gap-2" data-id="element-1521" data-mp="1jzkfjcsatxux">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-1522" data-mp="1k0m1wderm6ry">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] text-white text-sm font-medium transition-colors" data-id="element-1523" data-mp="2cozzis1xz12z">
                Create Job
              </button>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}
// --- Purchase Orders Panel ---
function PurchaseOrdersPanel({
  poRequired,
  setPoRequired,
  orders,
  total,
  onAdd,
  onRemove
}: {
  poRequired: boolean;
  setPoRequired: (v: boolean) => void;
  orders: PurchaseOrder[];
  total: number;
  onAdd: (po: PurchaseOrder) => void;
  onRemove: (id: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    setFile({
      name: selected.name,
      url: URL.createObjectURL(selected)
    });
  };
  const add = () => {
    if (!number.trim()) return;
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      number: number.trim(),
      description: description.trim() || undefined,
      amount: Number(amount) || 0,
      date,
      fileName: file?.name,
      fileUrl: file?.url
    });
    setNumber('');
    setDescription('');
    setAmount('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1524" data-mp="3fv3m47yzufao">
      <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-1525" data-mp="3um2t5ek6kvs5">
        <span className="text-sm font-medium text-slate-700" data-id="element-1526" data-mp="3pu2x3x78en0u">
          Purchase orders required for this client
        </span>
        <button type="button" role="switch" aria-checked={poRequired} onClick={() => setPoRequired(!poRequired)} className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${poRequired ? 'bg-[#14466B]' : 'bg-slate-300'}`} data-id="element-1527" data-mp="7fa7jyo83dw9">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${poRequired ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-1528" data-mp="cfoippl4f3yi" />
        </button>
      </label>

      {orders.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1529" data-mp="2e8w3heji6drs">
          <ShoppingCart className="w-10 h-10 text-slate-300 mb-3" data-id="element-1530" data-mp="1a9qw71zbqs87" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1531" data-mp="1a7w9bi4hsq1h">
            No purchase orders
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1532" data-mp="1f6pk32sea2xy">
            Add a purchase order below. Multiple POs can be tracked per job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1533" data-mp="mkxv6r3uhu6q">
          {orders.map(po => <div key={po.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1534" data-mp="rjr8aif7zj6d">
              <div className="min-w-0" data-id="element-1535" data-mp="vj019l2frd0v">
                <p className="text-sm font-medium text-slate-900" data-id="element-1536" data-mp="10bt586q2yhbw">
                  {po.number}
                </p>
                <p className="text-xs text-slate-500 truncate" data-id="element-1537" data-mp="3svkrxhsra4x">
                  {new Date(po.date).toLocaleDateString('en-GB')}
                  {po.description ? ` · ${po.description}` : ''}
                </p>
                {po.fileUrl && <a href={po.fileUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#14466B] hover:underline" data-id="element-1538" data-mp="9n0ciu0n516"><Paperclip className="h-3 w-3" data-id="element-1539" data-mp="3nfdz6wzlibzi" />{po.fileName || 'Open attachment'}</a>}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1540" data-mp="3igkjcxwu68w6">
                <span className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1541" data-mp="3diste9fr4wum">
                  {formatCurrency(po.amount)}
                </span>
                <button type="button" onClick={() => onRemove(po.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove purchase order" data-id="element-1542" data-mp="38q7yt5p0ufq1">
                  <Trash2 className="w-4 h-4" data-id="element-1543" data-mp="2zorrj6ti6tq1" />
                </button>
              </div>
            </div>)}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1544" data-mp="2utm76cfr1n42">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1545" data-mp="2purpxexfk6m2">
              Total
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-1546" data-mp="2l3w2kvczjkwx">
              {formatCurrency(total)}
            </span>
          </div>
        </div>}

      {/* Add PO */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1547" data-mp="2699dpmvpu6gk">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1548" data-mp="2b686o23n87d6">
          Add purchase order
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1549" data-mp="33uni6enkg7jr">
          <input type="text" value={number} onChange={e => setNumber(e.target.value)} className={drawerInputCls} placeholder="PO number *" data-id="element-1550" data-mp="33u3wqnypj10z" />
          <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} className={drawerInputCls} placeholder="Amount" data-id="element-1551" data-mp="2u4kg7oygvj9a" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1552" data-mp="3htmx2ula182q" />
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} className={drawerInputCls} placeholder="Description" data-id="element-1553" data-mp="3mmpu5cp9izno" />
          <div className="sm:col-span-2" data-id="element-1554" data-mp="167l16kj70o8h">
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={event => handleFile(event.target.files)} data-id="element-1555" data-mp="166jns9epfpnl" />
            <div className="flex min-h-[38px] items-center gap-2" data-id="element-1556" data-mp="1fwco2e9ecicj">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50" data-id="element-1557" data-mp="1fz8m02l8v70z"><Upload className="h-3.5 w-3.5" data-id="element-1558" data-mp="117q76844fij7" />{file ? 'Replace file' : 'Add file'}</button>
              {file && <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-slate-600" data-id="element-1559" data-mp="x5vaxi11rvca"><FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" data-id="element-1560" data-mp="2cn545bwbyaeb" /><span className="truncate" data-id="element-1561" data-mp="2clkdhxd4zblq">{file.name}</span></span>}
            </div>
          </div>
        </div>
        <button type="button" onClick={add} disabled={!number.trim()} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1562" data-mp="28nmlynvw8df6">
          <Plus className="w-4 h-4" data-id="element-1563" data-mp="fw15a80cb47h" />
          Add purchase order
        </button>
      </div>
    </div>;
}
function JobHistoryPanel({
  activity
}: {
  activity: JobActivity[];
}) {
  const orderedActivity = [...activity].sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
  return <div className="p-6" data-id="element-1564" data-mp="tn5wclpn6o67">
      <div className="mb-6" data-id="element-1565" data-mp="tqjzvxa1l3f8">
        <h3 className="text-base font-semibold text-slate-900" data-id="element-1566" data-mp="yntml561r0zd">Job history</h3>
        <p className="mt-0.5 text-xs text-slate-500" data-id="element-1567" data-mp="ylpl0tvmee29">
          A record of the key actions taken on this job.
        </p>
      </div>

      {orderedActivity.length === 0 ? <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center" data-id="element-1568" data-mp="30ghsjma2s2iu">
          <History className="mb-3 h-10 w-10 text-slate-300" data-id="element-1569" data-mp="1minae0wf3wsi" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1570" data-mp="1mkzzqz4i99fl">No activity yet</p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1571" data-mp="1hqm02kit8g54">
            Actions taken on this job will appear here.
          </p>
        </div> : <ol className="relative space-y-0 before:absolute before:bottom-5 before:left-4 before:top-5 before:w-px before:bg-slate-200" data-id="element-1572" data-mp="1do7gfy7bhccs">
          {orderedActivity.map(entry => <li key={entry.id} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-1573" data-mp="25gg90r00dkk3">
              <span className="relative z-10 mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-4 border-white bg-[#E8F0F5] text-[#14466B] shadow-sm" data-id="element-1574" data-mp="21h7irw46g4ta">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1575" data-mp="1rii0i28dcici" />
              </span>
              <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3" data-id="element-1576" data-mp="l2foterduphv">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-id="element-1577" data-mp="g2a64o33cdbx">
                  <div className="min-w-0" data-id="element-1578" data-mp="3k251ulooqg9r">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-1579" data-mp="3jz91yrta1hio">
                      {entry.action}
                    </p>
                    {entry.detail && <p className="mt-0.5 truncate text-xs text-slate-500" data-id="element-1580" data-mp="3t1s2h59lbi1r">
                        {entry.detail}
                      </p>}
                  </div>
                  <time className="flex-shrink-0 text-xs text-slate-400" dateTime={entry.occurredAt} data-id="element-1581" data-mp="1p79p0y4wrq5">
                    {formatJobActivityDate(entry.occurredAt)}
                  </time>
                </div>
                <p className="mt-2 text-xs text-slate-600" data-id="element-1582" data-mp="gkn2l2mzahky">
                  By <span className="font-medium text-slate-800" data-id="element-1583" data-mp="gleotmuor2yk">{entry.actor}</span>
                </p>
              </div>
            </li>)}
        </ol>}
    </div>;
}
function formatJobActivityDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// --- Financials Panel (read-only roll-up) ---
function FinancialsPanel({
  value,
  departments,
  currentUser,
  isAdmin,
  mileageEntries,
  onAddMileage,
  onRemoveMileage,
  timesheets,
  onAddTimesheet,
  onRemoveTimesheet,
  expenses,
  onAddExpense,
  onRemoveExpense,
  invoices,
  onAddInvoice,
  onRemoveInvoice
}: {
  value: number;
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  mileageEntries: MileageEntry[];
  onAddMileage: (m: MileageEntry) => void;
  onRemoveMileage: (id: string) => void;
  timesheets: TimesheetEntry[];
  onAddTimesheet: (t: TimesheetEntry) => void;
  onRemoveTimesheet: (id: string) => void;
  expenses: Expense[];
  onAddExpense: (e: Expense) => void;
  onRemoveExpense: (id: string) => void;
  invoices: Invoice[];
  onAddInvoice: (i: Invoice) => void;
  onRemoveInvoice: (id: string) => void;
}) {
  // Only one list section expanded at a time
  const [openSection, setOpenSection] = useState<'mileage' | 'timesheets' | 'expenses' | 'invoices' | null>(null);
  const expensesTotal = expenses.reduce((sum, e) => sum + e.value, 0);
  const invoiced = invoices.reduce((sum, i) => sum + i.value, 0);
  const totalMiles = mileageEntries.reduce((sum, m) => sum + m.miles, 0);
  const mileageValue = totalMiles * MILEAGE_RATE;
  const totalHours = timesheets.reduce((sum, entry) => sum + entry.hours, 0);
  const timeValue = timesheets.reduce((sum, entry) => sum + getTimesheetCost(entry), 0);
  // Total deductions taken against what's been invoiced
  const totalDeductions = expensesTotal + mileageValue + timeValue;
  // Realised profit is invoiced revenue less all deductions.
  const profit = invoiced - totalDeductions;
  const profitPositive = profit >= 0;
  // Budget profit applies the same deductions to the total job budget.
  const budgetProfit = value - totalDeductions;
  const budgetProfitPositive = budgetProfit >= 0;
  return <div className="p-6 space-y-5" data-id="element-1584" data-mp="d6zhrva7tz3k">
      <div data-id="element-1585" data-mp="3pnzbrpy3276a">
        <h3 className="text-base font-semibold text-slate-900" data-id="element-1586" data-mp="3pnpmk4kv441n">
          Financial roll-up
        </h3>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-1587" data-mp="3kqfwcmpnfocr">
          Job value less everything billed against it
        </p>
      </div>

      {/* 1. Job value / invoice — the headline figure everything deducts from */}
      <div className="rounded-xl border border-[#14466B]/20 bg-[#14466B]/[0.04] px-4 py-4" data-id="element-1588" data-mp="3um0ta6fv1yfn">
        <div className="flex items-center justify-between gap-3" data-id="element-1589" data-mp="cgo3ndnmc7gx">
          <div className="flex items-center gap-3 min-w-0" data-id="element-1590" data-mp="7je6fbtzmrij">
            <span className="w-10 h-10 rounded-lg bg-[#14466B] text-white flex items-center justify-center flex-shrink-0" data-id="element-1591" data-mp="7gryvc73kc0b">
              <PoundSterling className="w-5 h-5" data-id="element-1592" data-mp="h8xqgktfsrez" />
            </span>
            <div className="min-w-0" data-id="element-1593" data-mp="hascy3kcwrl6">
              <p className="text-sm font-semibold text-slate-900" data-id="element-1594" data-mp="v7xf3sgy96b5">Job value</p>
              <p className="text-xs text-slate-500" data-id="element-1595" data-mp="q9drlnmpjmgo">
                {invoices.length > 0 ? `${formatCurrency(invoiced)} invoiced to date` : 'Nothing invoiced yet'}
              </p>
            </div>
          </div>
          <span className="text-2xl font-bold text-[#14466B] tabular-nums flex-shrink-0" data-id="element-1596" data-mp="2pofcvx99h2bo">
            {formatCurrency(value)}
          </span>
        </div>
        {/* Invoiced is the realised slice of the job value — keep it directly under the headline */}
        <div className="mt-3 border-t border-[#14466B]/10 -mx-4 px-0" data-id="element-1597" data-mp="2yg7h3q7ozf7t">
          <FinancialAccordionRow icon={<FileText className="w-4 h-4" data-id="element-1599" data-mp="21skoy4hnij06" />} label="Invoiced" count={invoices.length} total={formatCurrency(invoiced)} open={openSection === 'invoices'} onToggle={() => setOpenSection(s => s === 'invoices' ? null : 'invoices')} data-id="element-1598" data-mp="21rrotmd76rr0">
            <InvoicesPanel invoices={invoices} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddInvoice} onRemove={onRemoveInvoice} data-id="element-1600" data-mp="2fqhpfbz879k4" />
          </FinancialAccordionRow>
        </div>
      </div>

      {/* 2. Deductions — everything taken off the job value, listed beneath it */}
      <div className="space-y-2" data-id="element-1601" data-mp="53xm9ren9v3l">
        <div className="px-1" data-id="element-1602" data-mp="52vzipo9nk61">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-1603" data-mp="56bdfp67en5v">
            Deductions
          </span>
        </div>
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1604" data-mp="3rj0645c32677">
          {/* Time logged (expandable) */}
          <FinancialAccordionRow icon={<Clock className="w-4 h-4" data-id="element-1606" data-mp="3iityh032g0fy" />} label="Time logged" count={timesheets.length} total={`−${formatCurrency(timeValue)}`} subTotal={`${totalHours}h`} open={openSection === 'timesheets'} onToggle={() => setOpenSection(s => s === 'timesheets' ? null : 'timesheets')} data-id="element-1605" data-mp="3ng3wv58fnmfx">
            <TimesheetsPanel timesheets={timesheets} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddTimesheet} onRemove={onRemoveTimesheet} data-id="element-1607" data-mp="38n0dcsba6ymk" />
          </FinancialAccordionRow>

          {/* Expenses (expandable) */}
          <FinancialAccordionRow icon={<Receipt className="w-4 h-4" data-id="element-1609" data-mp="12sh4x3dl5dnq" />} label="Expenses" count={expenses.length} total={`−${formatCurrency(expensesTotal)}`} open={openSection === 'expenses'} onToggle={() => setOpenSection(s => s === 'expenses' ? null : 'expenses')} data-id="element-1608" data-mp="12rplss16d8ec">
            <ExpensesPanel expenses={expenses} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddExpense} onRemove={onRemoveExpense} data-id="element-1610" data-mp="2na064nyep4hm" />
          </FinancialAccordionRow>

          {/* Mileage (expandable) */}
          <FinancialAccordionRow icon={<Car className="w-4 h-4" data-id="element-1612" data-mp="3f1z4fgz0sjdf" />} label="Mileage" count={mileageEntries.length} total={`−${formatCurrency(mileageValue)}`} subTotal={`${totalMiles.toLocaleString('en-GB')} mi`} open={openSection === 'mileage'} onToggle={() => setOpenSection(s => s === 'mileage' ? null : 'mileage')} data-id="element-1611" data-mp="3f28zhiyx9gpz">
            <MileagePanel entries={mileageEntries} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddMileage} onRemove={onRemoveMileage} data-id="element-1613" data-mp="3ay179bt99in0" />
          </FinancialAccordionRow>

          {/* Total deductions (summary row) */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50" data-id="element-1614" data-mp="1r2vp04l237h3">
            <p className="text-sm font-semibold text-slate-900" data-id="element-1615" data-mp="1n23e76wjcuip">
              Total deductions
            </p>
            <span className="text-sm font-bold text-rose-600 tabular-nums flex-shrink-0" data-id="element-1616" data-mp="2124f2mcsw1y6">
              −{formatCurrency(totalDeductions)}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Realised and budget profit use the same live deductions. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-1617" data-mp="13duca556xa60">
        <div className={`rounded-xl border px-4 py-4 ${profitPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-1618" data-mp="13ff8s0b2f36i">
          <div className="flex items-center justify-between gap-3" data-id="element-1619" data-mp="1dc09wajyru52">
            <div className="flex items-center gap-3 min-w-0" data-id="element-1620" data-mp="1dap6ibufkoeh">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${profitPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-1621" data-mp="ple5ct964rzt">
                {profitPositive ? <TrendingUp className="w-5 h-5" data-id="element-1622" data-mp="3vagzqw49zwwf" /> : <TrendingDown className="w-5 h-5" data-id="element-1623" data-mp="3vbignh7z3dm2" />}
              </span>
              <div className="min-w-0" data-id="element-1624" data-mp="43moy6jrre25">
                <p className={`text-sm font-semibold ${profitPositive ? 'text-emerald-800' : 'text-rose-800'}`} data-id="element-1625" data-mp="927d37lqy7a8">
                  {profitPositive ? 'Net profit' : 'Net loss'}
                </p>
                <p className={`text-xs ${profitPositive ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1626" data-mp="dyxwa3sulizv">
                  Invoiced less time, expenses &amp; mileage
                </p>
              </div>
            </div>
            <span className={`text-xl font-bold tabular-nums flex-shrink-0 ${profitPositive ? 'text-emerald-700' : 'text-rose-700'}`} data-id="element-1627" data-mp="rsyxr5b6pk3x">
              {profit < 0 ? `−${formatCurrency(Math.abs(profit))}` : formatCurrency(profit)}
            </span>
          </div>
        </div>
        <div className={`rounded-xl border px-4 py-4 ${budgetProfitPositive ? 'bg-blue-50 border-blue-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-1628" data-mp="11p1v7xv3cukj">
          <div className="flex items-center justify-between gap-3" data-id="element-1629" data-mp="2qar5bc62cleg">
            <div className="flex items-center gap-3 min-w-0" data-id="element-1630" data-mp="2m8xa3jen09lo">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${budgetProfitPositive ? 'bg-blue-100 text-[#14466B]' : 'bg-rose-100 text-rose-700'}`} data-id="element-1631" data-mp="2m7m5xiha4zu1">
                {budgetProfitPositive ? <TrendingUp className="w-5 h-5" data-id="element-1632" data-mp="2ccuaak79cn1w" /> : <TrendingDown className="w-5 h-5" data-id="element-1633" data-mp="39yzy9offkvf0" />}
              </span>
              <div className="min-w-0" data-id="element-1634" data-mp="3a0b0vkx53x4j">
                <p className={`text-sm font-semibold ${budgetProfitPositive ? 'text-[#14466B]' : 'text-rose-800'}`} data-id="element-1635" data-mp="34z585n3hc9v0">
                  {budgetProfitPositive ? 'Profit vs budget' : 'Loss vs budget'}
                </p>
                <p className={`text-xs ${budgetProfitPositive ? 'text-blue-700' : 'text-rose-600'}`} data-id="element-1636" data-mp="2v8sqq3qyowt3">
                  Job budget less time, expenses &amp; mileage
                </p>
              </div>
            </div>
            <span className={`text-xl font-bold tabular-nums flex-shrink-0 ${budgetProfitPositive ? 'text-[#14466B]' : 'text-rose-700'}`} data-id="element-1637" data-mp="3nwy8chmpy5op">
              {budgetProfit < 0 ? `−${formatCurrency(Math.abs(budgetProfit))}` : formatCurrency(budgetProfit)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
// Expandable roll-up row that reveals a list + inline add form when open
function FinancialAccordionRow({
  icon,
  label,
  count,
  total,
  subTotal,
  open,
  onToggle,
  children
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: string;
  subTotal?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <div data-id="element-1638" data-mp="1l8ezz6mgz7x9">
      <button type="button" onClick={onToggle} aria-expanded={open} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left" data-id="element-1639" data-mp="1l8597gtlowqi">
        <div className="flex items-center gap-3 min-w-0" data-id="element-1640" data-mp="1v7m8ngi9eee1">
          <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-1641" data-mp="1q8jeir3thsku">
            {icon}
          </span>
          <div className="min-w-0 flex items-center gap-2" data-id="element-1642" data-mp="29rqdbdamrbw6">
            <p className="text-sm font-medium text-slate-900" data-id="element-1643" data-mp="2eruy3fikmss7">{label}</p>
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-700" data-id="element-1644" data-mp="1zw5712yj3pgm">
              {count}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1645" data-mp="1r2jtfmy3oe8s">
          <span className="text-right" data-id="element-1646" data-mp="1vujyftt11xc8">
            <span className="block text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1647" data-mp="1vvlb22poyrw2">
              {total}
            </span>
            {subTotal && <span className="block text-xs text-slate-500 tabular-nums" data-id="element-1648" data-mp="1m48jlc7t23qk">
                {subTotal}
              </span>}
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-1649" data-mp="185imvxowe62q" />
        </div>
      </button>
      {open && <div className="border-t border-slate-100 bg-slate-50/50" data-id="element-1650" data-mp="1c7vr2hnzxvxe">
          {children}
        </div>}
    </div>;
}
// Collapsible "add new item" section — separates viewing the list from the
// action of adding to it. Shows a single trigger line until expanded.
function AddItemSection({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-[#14466B] hover:bg-[#14466B]/[0.04] hover:border-[#14466B]/40 transition-colors" data-id="element-1651" data-mp="1mpwswnqefg7">
        <Plus className="w-4 h-4" data-id="element-1652" data-mp="3nvhb14hz9v3z" />
        {label}
      </button>;
  }
  return <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1653" data-mp="3jr9mb7bisf5r">
      <div className="flex items-center justify-between" data-id="element-1654" data-mp="2cehl2fj3rsd4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1655" data-mp="2cbwkdtujjigy">
          {label}
        </h3>
        <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1656" data-mp="nuvd2jmk5l4a">
          <X className="w-3.5 h-3.5" data-id="element-1657" data-mp="rtuazk9vghm8" />
          Cancel
        </button>
      </div>
      {children}
    </div>;
}
// --- Mileage Panel ---
function MileagePanel({
  entries,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  entries: MileageEntry[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (m: MileageEntry) => void;
  onRemove: (id: string) => void;
}) {
  const [person, setPerson] = useState(currentUser);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [miles, setMiles] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const numericMiles = Number(miles);
  const canAdd = !!person.trim() && numericMiles > 0 && !!department;
  const add = () => {
    if (!person.trim()) {
      setFormError('Enter who claimed the mileage.');
      return;
    }
    if (!miles || numericMiles <= 0) {
      setFormError('Enter the number of miles.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      person: person.trim(),
      date,
      miles: numericMiles,
      department,
      notes: notes.trim() || undefined
    });
    setPerson(currentUser);
    setDate(new Date().toISOString().split('T')[0]);
    setMiles('');
    setDepartment('');
    setNotes('');
    setFormError(null);
  };
  return <div className="p-6 space-y-5" data-id="element-1658" data-mp="2dae3k9uy8em4">
      {/* Mileage list */}
      {entries.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1659" data-mp="1pkjs9wqa2y3z">
          <Car className="w-10 h-10 text-slate-300 mb-3" data-id="element-1660" data-mp="1klqee372x53a" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1661" data-mp="3o842hpikcs">
            No mileage claimed
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1662" data-mp="4vptqf0rlnfh">
            Add mileage below to claim it against this job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1663" data-mp="3mcz8y4cjtckx">
          {entries.map(m => {
        const canDelete = isAdmin || m.person === currentUser;
        return <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1664" data-mp="iv8rqezv3ie2">
                <div className="min-w-0" data-id="element-1665" data-mp="9u118j3l7luj">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1666" data-mp="9t7sxdozcoaa">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1667" data-mp="dul0ptbmv1ju">
                      {m.person}
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1668" data-mp="2uif1s2d7yhhv">
                      {m.department}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-1669" data-mp="154xifbxny4w0">
                    {new Date(m.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                    {m.notes ? ` · ${m.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1670" data-mp="mcwehv617ofq">
                  <span className="text-right" data-id="element-1671" data-mp="ma0iicdqv9tx">
                    <span className="block text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1672" data-mp="hc8n38dw55kz">
                      {m.miles.toLocaleString('en-GB')} mi
                    </span>
                    <span className="block text-xs text-slate-500 tabular-nums" data-id="element-1673" data-mp="7fm8v2g2lcrx">
                      {formatCurrency(m.miles * MILEAGE_RATE)}
                    </span>
                  </span>
                  {canDelete && <button type="button" onClick={() => onRemove(m.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Delete mileage entry" data-id="element-1674" data-mp="21svf8ldbki5y">
                      <Trash2 className="w-4 h-4" data-id="element-1675" data-mp="2eao5w1ph54h1" />
                    </button>}
                </div>
              </div>;
      })}
        </div>}

      {/* Add mileage */}
      <AddItemSection label="Add new mileage" data-id="element-1676" data-mp="2o19cabbe3dai">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1677" data-mp="2o10xgdvphxwo">
          <div data-id="element-1678" data-mp="1rbruwz31o2sz">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1679" data-mp="1rcbeslc9919n">
              Person *
            </label>
            <input type="text" value={person} onChange={e => {
            setPerson(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="Who claimed the mileage" data-id="element-1680" data-mp="1vddet4dzxhco" />
          </div>
          <div data-id="element-1681" data-mp="258oo376mxwd1">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1682" data-mp="3brdcs9ta6wxa">
              Miles *
            </label>
            <input type="number" min="0" step="1" value={miles} onChange={e => {
            setMiles(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0" data-id="element-1683" data-mp="3gpew8vumcy9a" />
          </div>
          <div data-id="element-1684" data-mp="1ou8f8ol5yxd">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1685" data-mp="1p3w2pm5rtzw">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1686" data-mp="anxtujb86qf1">
              <option value="" data-id="element-1687" data-mp="pb0s4s7dh33f">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1688" data-mp="kd8wpo7iqyuh">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1689" data-mp="tefhwal44xu2">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1690" data-mp="tep03bzx20p7">
              Date
            </label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1691" data-mp="3043kyd40jrsi" />
          </div>
          <div className="sm:col-span-2" data-id="element-1692" data-mp="2v7d5ns019085">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1693" data-mp="1anrlg7ch8gj1">
              Notes
            </label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={drawerInputCls} placeholder="Optional notes" data-id="element-1694" data-mp="1fndyafoe7urb" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1695" data-mp="15wt1mxsa50b1">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1696" data-mp="15uom1il0qcux" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1697" data-mp="rzcsheilmsfl">
          <Plus className="w-4 h-4" data-id="element-1698" data-mp="mxf280bs6xd5" />
          Add mileage
        </button>
      </AddItemSection>
    </div>;
}
// --- Timesheets Panel ---
function TimesheetsPanel({
  timesheets,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  timesheets: TimesheetEntry[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (t: TimesheetEntry) => void;
  onRemove: (id: string) => void;
}) {
  const {
    users
  } = useSettingsStore();
  const activeUsers = users.filter(user => user.status === 'active');
  const defaultUser = activeUsers.find(user => `${user.firstName} ${user.lastName}` === currentUser) || activeUsers[0];
  const [userId, setUserId] = useState(defaultUser?.id || '');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const numericHours = Number(hours);
  const selectedUser = activeUsers.find(user => user.id === userId);
  const canAdd = !!selectedUser && numericHours > 0 && !!department;
  const add = () => {
    if (!selectedUser) {
      setFormError('Select who logged the time.');
      return;
    }
    if (!hours || numericHours <= 0) {
      setFormError('Enter the number of hours.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      person: `${selectedUser.firstName} ${selectedUser.lastName}`,
      date,
      hours: numericHours,
      department,
      hourlyCost: selectedUser.hourlyCost,
      notes: notes.trim() || undefined
    });
    setUserId(defaultUser?.id || '');
    setDate(new Date().toISOString().split('T')[0]);
    setHours('');
    setDepartment('');
    setNotes('');
    setFormError(null);
  };
  return <div className="p-6 space-y-5" data-id="element-1699" data-mp="28717qgcppy2p">
      {/* Timesheet list */}
      {timesheets.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1700" data-mp="23cx15y7czs25">
          <Clock className="w-10 h-10 text-slate-300 mb-3" data-id="element-1701" data-mp="1bnk5cqes42hp" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1702" data-mp="1bkxw8mc8pexd">No time logged</p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1703" data-mp="3ar906i42gus8">
            Add a timesheet entry below to book time against this job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1704" data-mp="3khvca2ckr366">
          {timesheets.map(t => {
        const canDelete = isAdmin || t.person === currentUser;
        return <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1705" data-mp="2rt69zzzs8rsu">
                <div className="min-w-0" data-id="element-1706" data-mp="2wssla3w1vxz0">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1707" data-mp="31r3ucceiu4g6">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1708" data-mp="31psq6bh5yuoj">
                      {t.person}
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1709" data-mp="h0vafj99noqv">
                      {t.department}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-1710" data-mp="26iicxmmfc3qe">
                    {new Date(t.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                    {t.notes ? ` · ${t.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1711" data-mp="1rskwjsuqkibr">
                  <span className="text-right tabular-nums" data-id="element-1712" data-mp="1mu9l5doslzrf">
                    <span className="block text-sm font-semibold text-slate-900" data-id="element-1713" data-mp="1isforhlmrhx2">{t.hours}h</span>
                    <span className="block text-xs text-slate-500" data-id="element-1714" data-mp="1iuscychzeoik">£{(t.hourlyCost ?? LEGACY_HOURLY_COST).toFixed(2)}/hr · {formatCurrency(getTimesheetCost(t))}</span>
                  </span>
                  {canDelete && <button type="button" onClick={() => onRemove(t.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Delete timesheet entry" data-id="element-1715" data-mp="387cch6ap3x46">
                      <Trash2 className="w-4 h-4" data-id="element-1716" data-mp="20v4oaw70q9u7" />
                    </button>}
                </div>
              </div>;
      })}
        </div>}

      {/* Add timesheet */}
      <AddItemSection label="Log new time" data-id="element-1717" data-mp="24ucditr7ce6v">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1718" data-mp="1qx72sggjmr5a">
          <div data-id="element-1719" data-mp="1qz1kllwiq355">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1720" data-mp="1vvi8qg3pwhi8">
              Person *
            </label>
            <select value={userId} onChange={e => {
            setUserId(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1721" data-mp="1h6m2mol81ek9">
              <option value="" data-id="element-1722" data-mp="186631q7lmrko">Select a user…</option>
              {activeUsers.map(user => <option key={user.id} value={user.id} data-id="element-1723" data-mp="18432wdcqfi28">{user.firstName} {user.lastName} · £{user.hourlyCost.toFixed(2)}/hr</option>)}
            </select>
          </div>
          <div data-id="element-1724" data-mp="y91exku70m18">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1725" data-mp="y8jjurj9oduw">
              Hours *
            </label>
            <input type="number" min="0" step="0.25" value={hours} onChange={e => {
            setHours(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0" data-id="element-1726" data-mp="13978j6k0wily" />
          </div>
          <div data-id="element-1727" data-mp="tj7f457yafap">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1728" data-mp="tjqxfn1ij5p9">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1729" data-mp="ojmgjvwp08yi">
              <option value="" data-id="element-1730" data-mp="yjd9vu9iohx0">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1731" data-mp="1c9p3jxlr2oq9">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1732" data-mp="17c6t22s341g5">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1733" data-mp="17bnlng028vgd">
              Date
            </label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1734" data-mp="1m61syja9igf7" />
          </div>
          {selectedUser && <div className="flex items-end pb-0.5 text-xs text-slate-500" data-id="element-1735" data-mp="1od185n0etz9b">
              Time will be costed at <span className="ml-1 font-semibold text-slate-700" data-id="element-1736" data-mp="1takxpj2k5ygk">£{selectedUser.hourlyCost.toFixed(2)}/hr</span>.
            </div>}
          <div className="sm:col-span-2" data-id="element-1737" data-mp="1y8uu1x00r53r">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1738" data-mp="2315ud0yo8swu">
              Notes
            </label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={drawerInputCls} placeholder="Optional notes" data-id="element-1739" data-mp="280s7zbiewb66" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1740" data-mp="2h16taavdlq9o">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1741" data-mp="2gz441t21s160" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1742" data-mp="2lxxj3pwzg08a">
          <Plus className="w-4 h-4" data-id="element-1743" data-mp="k2pzbrefkzlr" />
          Log time
        </button>
      </AddItemSection>
    </div>;
}
// --- Invoices Panel ---
function InvoicesPanel({
  invoices,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  invoices: Invoice[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (i: Invoice) => void;
  onRemove: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<InvoiceCategory>('Interim');
  const [department, setDepartment] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<InvoicePaymentStatus>('unpaid');
  const [sageId, setSageId] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const total = invoices.filter(invoice => invoice.paymentStatus !== 'cancelled').reduce((sum, invoice) => sum + invoice.value, 0);
  const numericValue = Number(value);
  const canAdd = !!value && numericValue !== 0 && !!department;
  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    setFile({
      name: selected.name,
      url: URL.createObjectURL(selected)
    });
  };
  const add = () => {
    if (!value || numericValue === 0) {
      setFormError('Enter a non-zero value.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      value: category === 'Credit note' ? -Math.abs(numericValue) : Math.abs(numericValue),
      category,
      department,
      paymentStatus: category === 'Credit note' ? 'paid' : paymentStatus,
      sageId: sageId.trim() || undefined,
      description: description.trim() || undefined,
      fileName: file?.name,
      fileUrl: file?.url,
      addedBy: currentUser,
      date: new Date().toISOString()
    });
    setValue('');
    setCategory('Interim');
    setDepartment('');
    setPaymentStatus('unpaid');
    setSageId('');
    setDescription('');
    setFile(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1744" data-mp="2ycltvbmb82cv">
      {invoices.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1745" data-mp="2algeexka7c33">
          <FileText className="w-10 h-10 text-slate-300 mb-3" data-id="element-1746" data-mp="2kiuqkkz69epl" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1747" data-mp="2kk5worg3ayjv">
            No invoices raised
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1748" data-mp="2flun8htpiq26">
            Raise separate invoice entries for each department.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1749" data-mp="3qzgafxg0ahl2">
          {invoices.map(invoice => {
        const canDelete = isAdmin || invoice.addedBy === currentUser;
        const isCredit = invoice.value < 0;
        const isCancelled = invoice.paymentStatus === 'cancelled';
        const invoiceDetails = <>
                <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1750" data-mp="3vpeifbp61ova">
                  <FileText className="w-4 h-4" data-id="element-1751" data-mp="s74qapl8y6jw" />
                </span>
                <div className="min-w-0" data-id="element-1752" data-mp="s8fsikz1n68w">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1753" data-mp="n78p0y3xot81">
                    <span className={`text-sm font-semibold tabular-nums ${isCredit ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1754" data-mp="igjv7c4s7k5q">
                      {isCredit ? `−${formatCurrency(Math.abs(invoice.value))}` : formatCurrency(invoice.value)}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium" data-id="element-1755" data-mp="1aye1r4ekv5e4">
                      <Tag className="w-3 h-3" data-id="element-1756" data-mp="2otnrerdtu7jd" />
                      {invoice.category}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1757" data-mp="2tt8sp8ggyrz9">
                      {invoice.department}
                    </span>
                    {!isCredit && <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${isCancelled ? 'bg-slate-200 text-slate-600' : invoice.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-1758" data-mp="32ugnvhnovcp4">
                        {isCancelled ? 'Cancelled' : invoice.paymentStatus === 'paid' ? 'Paid' : 'Outstanding'}
                      </span>}
                    {invoice.fileUrl && <span className="inline-flex items-center gap-1 text-[11px] text-[#14466B] font-medium group-hover:underline" data-id="element-1759" data-mp="3ll7vado48t0v">
                        <ExternalLink className="w-3 h-3" data-id="element-1760" data-mp="3vh1h6nnq5yvb" />
                        Attachment
                      </span>}
                  </div>
                  {invoice.description && <p className="mt-1 text-xs text-slate-600" data-id="element-1761" data-mp="1vcyup43hwhwg">{invoice.description}</p>}
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1762" data-mp="1qe5jjibol2zg">
                    {invoice.addedBy} ·{' '}
                    {new Date(invoice.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}{invoice.sageId ? ` · Sage ID: ${invoice.sageId}` : ''}
                  </p>
                </div>
              </>;
        return <div key={invoice.id} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1763" data-mp="2a6k6y3o3p1sz">
                {invoice.fileUrl ? <a href={invoice.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 min-w-0 flex-1" title="Open attachment in a new tab" data-id="element-1764" data-mp="2j67b2x87slpq">
                    {invoiceDetails}
                  </a> : <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-1765" data-mp="ospj8y0s12ow">
                    {invoiceDetails}
                  </div>}
                {canDelete && <button type="button" onClick={() => onRemove(invoice.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" aria-label="Delete invoice" data-id="element-1766" data-mp="35dukktmr5urn">
                    <Trash2 className="w-4 h-4" data-id="element-1767" data-mp="2vmgge8juw6s6" />
                  </button>}
              </div>;
      })}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1768" data-mp="2hmp5whsjt8ix">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1769" data-mp="2mn3go7ljawkn">
              Active invoice total
            </span>
            <span className={`text-sm font-bold tabular-nums ${total < 0 ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1770" data-mp="27rniprhu2giz">
              {total < 0 ? `−${formatCurrency(Math.abs(total))}` : formatCurrency(total)}
            </span>
          </div>
        </div>}

      <AddItemSection label="Add new invoice" data-id="element-1771" data-mp="axiypaseeqrv">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1772" data-mp="av6j34a44shz">
          <div data-id="element-1773" data-mp="26zdwqejlon1l">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1774" data-mp="26zvuxgptpbc5">
              Value *
            </label>
            <input type="number" min="0" step="0.01" value={value} onChange={e => {
            setValue(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0.00" data-id="element-1775" data-mp="22wpni5qz5e43" />
            {category === 'Credit note' && <p className="mt-1 text-xs text-rose-600" data-id="element-1776" data-mp="1o1t898kqzqhi">
                Saved as a negative value and deducted from the invoice total.
              </p>}
          </div>
          <div data-id="element-1777" data-mp="1f997a1gzh9ti">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1778" data-mp="1f8pzveoym3tq">
              Category *
            </label>
            <select value={category} onChange={e => setCategory(e.target.value as InvoiceCategory)} className={drawerInputCls} data-id="element-1779" data-mp="1ab7nfebqh6gz">
              {INVOICE_CATEGORIES.map(item => <option key={item} value={item} data-id="element-1780" data-mp="10byw6t8965yb">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1781" data-mp="q70f0ot97kfq">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1782" data-mp="q7ijg8q2l4yq">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1783" data-mp="v97w1ssxxqox">
              <option value="" data-id="element-1784" data-mp="154ss7abbvwqr">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1785" data-mp="7ilnm0f0y4ee">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1786" data-mp="cej4q8y43es8">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1787" data-mp="ceauypxacpl9">
              Payment status *
            </label>
            <select value={category === 'Credit note' ? 'paid' : paymentStatus} onChange={e => setPaymentStatus(e.target.value as InvoicePaymentStatus)} disabled={category === 'Credit note'} className={`${drawerInputCls} disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed`} data-id="element-1788" data-mp="m6yjkxe43n8w">
              <option value="unpaid" data-id="element-1789" data-mp="3huc936oec17e">Outstanding</option>
              <option value="paid" data-id="element-1790" data-mp="3hsra94v1tw3q">Paid</option>
              <option value="cancelled" data-id="element-1791" data-mp="3cwj0y83j8x4h">Cancelled</option>
            </select>
          </div>
          <div data-id="element-1792" data-mp="3rkx1udhe2bie">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1793" data-mp="3rjm1knn5jlw1">
              Sage ID <span className="text-slate-400" data-id="element-1794" data-mp="3mlape69dwzan">(optional)</span>
            </label>
            <input type="text" value={sageId} onChange={e => setSageId(e.target.value)} className={drawerInputCls} placeholder="e.g. SI-2026-001" data-id="element-1795" data-mp="2yvgcxpsz9cqx" />
          </div>
          <div className="sm:col-span-2" data-id="element-1796" data-mp="2tyzr52993ah0">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1797" data-mp="38up6ihjxkpcr">
              Description <span className="text-slate-400" data-id="element-1798" data-mp="33ujjjeolvr10">(optional)</span>
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={`${drawerInputCls} resize-y`} placeholder="Add a short description for this invoice" data-id="element-1799" data-mp="o3wyfpyfmp0z" />
          </div>
          <div data-id="element-1800" data-mp="qomlhhofn9x5">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1801" data-mp="qo3e2uwes3xd">
              Attachment <span className="text-slate-400" data-id="element-1802" data-mp="vnz944n4ol0s">(optional)</span>
            </label>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => handleFile(e.target.files)} data-id="element-1803" data-mp="hnojf4xk1ch7" />
            <div className="flex items-center gap-2 min-h-[38px]" data-id="element-1804" data-mp="lq1m1kh08u9r">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors" data-id="element-1805" data-mp="19echu97ug3r6">
                <Upload className="w-3.5 h-3.5" data-id="element-1806" data-mp="10maike9igtih" />
                {file ? 'Replace file' : 'Upload file'}
              </button>
              {file && <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-slate-600" data-id="element-1807" data-mp="1sdyrsrwzszx3">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" data-id="element-1808" data-mp="1x46rkhwv9c77" />
                  <span className="truncate" data-id="element-1809" data-mp="1x69t9z7dstrr">{file.name}</span>
                </span>}
            </div>
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1810" data-mp="13msvpxegb4sa">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1811" data-mp="ynpxowww1ytt" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1812" data-mp="towlr8x92fvr">
          <Plus className="w-4 h-4" data-id="element-1813" data-mp="1mevcr9v08n32" />
          Add invoice
        </button>
      </AddItemSection>
    </div>;
}
// --- Expenses Panel ---
function ExpensesPanel({
  expenses,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  expenses: Expense[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (e: Expense) => void;
  onRemove: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<ExpenseCategory | ''>('');
  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');
  const [receipt, setReceipt] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const total = expenses.reduce((sum, e) => sum + e.value, 0);
  const numericValue = Number(value);
  const canAdd = !!value && numericValue > 0 && !!category && !!department && !!receipt;
  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setReceipt({
      name: file.name,
      url: URL.createObjectURL(file)
    });
    setFormError(null);
  };
  const add = () => {
    if (!value || numericValue <= 0) {
      setFormError('Enter a value greater than 0.');
      return;
    }
    if (!category) {
      setFormError('Select a category.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    if (!receipt) {
      setFormError('Attach a receipt file or image.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      value: numericValue,
      category,
      department,
      description: description.trim() || undefined,
      receiptName: receipt.name,
      receiptUrl: receipt.url,
      addedBy: currentUser,
      date: new Date().toISOString()
    });
    setValue('');
    setCategory('');
    setDepartment('');
    setDescription('');
    setReceipt(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1814" data-mp="at8vxf9ubnh7">
      {/* Expense list */}
      {expenses.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1815" data-mp="3agz186snkswn">
          <Receipt className="w-10 h-10 text-slate-300 mb-3" data-id="element-1816" data-mp="3eh11cbiqpe9t" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1817" data-mp="36zkylxkujzsr">
            No expenses raised
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1818" data-mp="31zq8vxct5g9a">
            Add an expense below. Each expense needs a value, category and
            receipt.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1819" data-mp="2sza9ayz6qt9p">
          {expenses.map(exp => {
        const canDelete = isAdmin || exp.addedBy === currentUser;
        return <div key={exp.id} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1820" data-mp="2j9qp9pzqktd9">
                <a href={exp.receiptUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 min-w-0 flex-1" title="Open receipt in a new tab" data-id="element-1821" data-mp="2eb70zixo75hq">
                  <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1822" data-mp="25a8umtzrelvz">
                    <Receipt className="w-4 h-4" data-id="element-1823" data-mp="20b4mm6okmu78" />
                  </span>
                  <div className="min-w-0" data-id="element-1824" data-mp="1qmqwkegpcpij">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-1825" data-mp="1qp3dcoaq4tu0">
                      <span className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1826" data-mp="1vmv43ezmu9wm">
                        {formatCurrency(exp.value)}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium" data-id="element-1827" data-mp="1lne3hbz8mm7i">
                        <Tag className="w-3 h-3" data-id="element-1828" data-mp="29fl44nbzxfcq" />
                        {exp.category}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1829" data-mp="2eefo3slp99yf">
                        {exp.department}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#14466B] font-medium group-hover:underline" data-id="element-1830" data-mp="2scmb2dp41pjb">
                        <ExternalLink className="w-3 h-3" data-id="element-1831" data-mp="2x44agxbhji26" />
                        Receipt
                      </span>
                    </div>
                    {exp.description && <p className="text-sm text-slate-700 mt-1 truncate" data-id="element-1832" data-mp="gpaed6oshjeq">
                        {exp.description}
                      </p>}
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1833" data-mp="vclqpassp7fw">
                      {exp.addedBy} ·{' '}
                      {new Date(exp.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                    </p>
                  </div>
                </a>
                {canDelete && <button type="button" onClick={() => onRemove(exp.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" aria-label="Delete expense" data-id="element-1834" data-mp="bq63mbm7vdma">
                    <Trash2 className="w-4 h-4" data-id="element-1835" data-mp="1n3rtjyzwhj8v" />
                  </button>}
              </div>;
      })}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1836" data-mp="1j2ptjfy5t35u">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1837" data-mp="2eepmzdlfs7ml">
              Total
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-1838" data-mp="2ifj8y1fbjccu">
              {formatCurrency(total)}
            </span>
          </div>
        </div>}

      {/* Add expense */}
      <AddItemSection label="Add new expense" data-id="element-1839" data-mp="2sf0bwb36rc54">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1840" data-mp="2xcs312w0au89">
          <div data-id="element-1841" data-mp="2xafpd1xa7610">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1842" data-mp="2xapdsl2oh54l">
              Value *
            </label>
            <input type="number" min="0" step="0.01" value={value} onChange={e => {
            setValue(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0.00" data-id="element-1843" data-mp="3653qcfvtq1k0" />
          </div>
          <div data-id="element-1844" data-mp="3g4b6faty0aat">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1845" data-mp="3g4kuutzca9ee">
              Category *
            </label>
            <select value={category} onChange={e => {
            setCategory(e.target.value as ExpenseCategory);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1846" data-mp="3kzqh5twnlq30">
              <option value="" data-id="element-1847" data-mp="14hr79k6s4bz5">Select a category…</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c} data-id="element-1848" data-mp="zh3easyzpl2a">
                  {c}
                </option>)}
            </select>
          </div>
          <div data-id="element-1849" data-mp="ujuyv0587v4q">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1850" data-mp="1s4zcy384dn1p">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1851" data-mp="1n5w7wis53gtq">
              <option value="" data-id="element-1852" data-mp="1dgmbwru6de0c">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1853" data-mp="1dei809wa0f02">
                  {item}
                </option>)}
            </select>
          </div>
          <div className="sm:col-span-2" data-id="element-1854" data-mp="264rx1fp5550p">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1855" data-mp="263p5r6nwp0k9">
              Receipt *
            </label>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => handleFile(e.target.files)} data-id="element-1856" data-mp="96kxcw09x7oa" />
            <div className="flex items-center gap-2" data-id="element-1857" data-mp="3qm12ew28jugz">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors" data-id="element-1858" data-mp="3vm5lmtuj33aw">
                <Upload className="w-3.5 h-3.5" data-id="element-1859" data-mp="3lor7j0w2uqlr" />
                {receipt ? 'Replace file' : 'Upload file / image'}
              </button>
              {receipt && <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-slate-600" data-id="element-1860" data-mp="3cn9ujrdyv51a">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" data-id="element-1861" data-mp="15wtv4btna9bi" />
                  <span className="truncate" data-id="element-1862" data-mp="15uqznc9q3nze">{receipt.name}</span>
                </span>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-1863" data-mp="2vdyynasr9vzf">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1864" data-mp="2veqriduyxfm0">
              Description
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className={drawerInputCls} placeholder="Optional description" data-id="element-1865" data-mp="2qgp8tu1gfib2" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1866" data-mp="30az17bqecgls">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1867" data-mp="3e35l98wo77qi" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1868" data-mp="394c67c1qj8o8">
          <Plus className="w-4 h-4" data-id="element-1869" data-mp="1tolokvh5yghn" />
          Add expense
        </button>
      </AddItemSection>
    </div>;
}
// --- Job Notes & Files Panel ---
function JobNotesPanel({
  notes,
  attachments,
  newNote,
  setNewNote,
  onAddNote,
  onAddAttachment,
  onRemoveAttachment
}: {
  notes: JobNote[];
  attachments: Attachment[];
  newNote: string;
  setNewNote: (v: string) => void;
  onAddNote: () => void;
  onAddAttachment: (a: Attachment) => void;
  onRemoveAttachment: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(f => {
      onAddAttachment({
        id: Math.random().toString(36).substr(2, 9),
        name: f.name,
        size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`,
        uploadedAt: new Date().toISOString()
      });
    });
  };
  return <div className="p-6 space-y-6" data-id="element-1870" data-mp="xyl3pwis6e1a">
      {/* Files */}
      <div data-id="element-1871" data-mp="25bd64rmxp6vi">
        <div className="flex items-center justify-between mb-2" data-id="element-1872" data-mp="259s8uu98j9ty">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1873" data-mp="29cf7be0evt04">
            <Paperclip className="w-4 h-4" data-id="element-1874" data-mp="3tuzdqmfkhc3t" />
            Attachments
          </h3>
          <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 text-sm font-medium text-[#14466B] hover:underline" data-id="element-1875" data-mp="3pt4erol3zat7">
            <Upload className="w-3.5 h-3.5" data-id="element-1876" data-mp="3l01ih8oy5n9b" />
            Upload
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFiles(e.target.files)} data-id="element-1877" data-mp="3g2jhq5l32ece" />
        </div>
        {attachments.length === 0 ? <p className="text-sm text-slate-400 py-2" data-id="element-1878" data-mp="364dffmvd7670">No files attached yet.</p> : <div className="space-y-2" data-id="element-1879" data-mp="323a33acizsaj">
            {attachments.map(a => <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors group" data-id="element-1880" data-mp="323tles638ip3">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-1881" data-mp="10cgiu9rlemw7" />
                <div className="min-w-0 flex-1" data-id="element-1882" data-mp="109khebgjdg30">
                  <p className="text-sm text-slate-800 truncate" data-id="element-1883" data-mp="vdvepz97zj2h">{a.name}</p>
                  <p className="text-xs text-slate-400" data-id="element-1884" data-mp="1lm7r8ymzh1kg">{a.size}</p>
                </div>
                <button type="button" onClick={() => onRemoveAttachment(a.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove attachment" data-id="element-1885" data-mp="1gnwhenwour28">
                  <X className="w-4 h-4" data-id="element-1886" data-mp="1qcy1ap6ctslh" />
                </button>
              </div>)}
          </div>}
      </div>

      {/* Notes */}
      <div data-id="element-1887" data-mp="12of2r1d3wu6l">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-1888" data-mp="12o59n4wrm6wo">
          <StickyNote className="w-4 h-4" data-id="element-1889" data-mp="xr56b1ravi99" />
          Notes
        </h3>
        <div className="space-y-2" data-id="element-1890" data-mp="1bq4s81nfhj1q">
          <textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={2} className={`${drawerInputCls} resize-none`} placeholder="Add a project note..." data-id="element-1891" data-mp="16pgtsuwvdzxh" />
          <button type="button" onClick={onAddNote} disabled={!newNote.trim()} className="px-4 py-2 rounded-lg bg-[#14466B] hover:bg-[#0F3D5C] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1892" data-mp="2n1vwg8i0nsj6">
            Add note
          </button>
        </div>
        <div className="mt-4 space-y-3" data-id="element-1893" data-mp="2uokkj3gaay94">
          {notes.length === 0 ? <p className="text-sm text-slate-400" data-id="element-1894" data-mp="2go9v84umhrq2">No notes yet.</p> : notes.map(note => <article key={note.id} className="border border-slate-200 rounded-xl p-4 bg-white" data-id="element-1895" data-mp="2lmbb6gwr5axb">
                <header className="flex items-center justify-between mb-2" data-id="element-1896" data-mp="39ce1arh3w6ta">
                  <div className="flex items-center gap-2 min-w-0" data-id="element-1897" data-mp="39ei230jpkppc">
                    <div className="w-7 h-7 rounded-full bg-[#14466B] text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0" data-id="element-1898" data-mp="3eapaczzkudag">
                      {note.author.split(' ').map(s => s[0]).slice(0, 2).join('')}
                    </div>
                    <span className="text-sm font-medium text-slate-900 truncate" data-id="element-1899" data-mp="3sahx6k7zg9d5">
                      {note.author}
                    </span>
                  </div>
                  <time className="text-xs text-slate-400 tabular-nums flex-shrink-0" data-id="element-1900" data-mp="y5ho8wkaqd9">
                    {new Date(note.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                  </time>
                </header>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-id="element-1901" data-mp="3sygzl6ff0mv6">
                  {note.body}
                </p>
              </article>)}
        </div>
      </div>
    </div>;
}
function getPersonInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
function PersonAvatar({
  name,
  roleLabel,
  tone
}: {
  name: string;
  roleLabel: string;
  tone: 'director' | 'manager';
}) {
  const {
    users
  } = useSettingsStore();
  const match = users.find(user => `${user.firstName} ${user.lastName}`.toLowerCase() === name.trim().toLowerCase());
  const label = `${roleLabel}: ${name || 'Unassigned'}`;
  const toneCls = tone === 'director' ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-[#14466B]';
  if (!name.trim()) {
    return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-[11px] font-semibold text-slate-300" title={`${roleLabel}: Unassigned`} data-id="element-1902" data-mp="1opta7t0rkbfz">
        —<span className="sr-only" data-id="element-1903" data-mp="wyn6z3pqxxmh">{`${roleLabel} unassigned`}</span>
      </span>;
  }
  return <span className={`inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-white text-[11px] font-semibold ${toneCls}`} title={label} data-id="element-1904" data-mp="112l6hfizhag2">
      {match?.profileUrl ? <img src={match.profileUrl} alt="" className="h-full w-full object-cover" data-id="element-1905" data-mp="1atfsqjw8ca1i" /> : getPersonInitials(name)}
      <span className="sr-only" data-id="element-1906" data-mp="dao6clowc6us">{label}</span>
    </span>;
}
// --- Department Budget Allocations Tab ---
type BudgetAllocationsTabProps = {
  jobs: Job[];
  onOpenJob: (job: Job) => void;
  onUpdateBudget: (jobId: string, department: string, monthKey: string, amount: number) => void;
  onValidateBudget: (jobId: string, department: string, monthKey: string) => void;
  onUnvalidateBudget: (jobId: string, department: string, monthKey: string) => void;
  showValidatedOnly: boolean;
};
function BudgetAllocationsTab({
  jobs,
  onOpenJob,
  onUpdateBudget,
  onValidateBudget,
  onUnvalidateBudget,
  showValidatedOnly
}: BudgetAllocationsTabProps) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(() => new Set());
  const months = useMemo(() => {
    const now = new Date();
    return [-1, 0, 1].map(offset => {
      const date = new Date(now.getFullYear(), now.getMonth() + monthOffset + offset, 1);
      return {
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-GB', {
          month: 'short',
          year: 'numeric'
        })
      };
    });
  }, [monthOffset]);
  const getDepartmentTotal = (job: Job, department: string) => Object.values(job.departmentBudgets?.[department] || {}).reduce((sum, amount) => sum + amount, 0);
  const getJobTotal = (job: Job) => job.departments.reduce((sum, department) => sum + getDepartmentTotal(job, department), 0);
  const isDepartmentMonthValidated = (job: Job, department: string, monthKey: string) => job.validatedDepartmentBudgets?.[department]?.[monthKey] === true;
  const isJobMonthValidated = (job: Job, monthKey: string) => {
    const departmentsWithAllocations = job.departments.filter(department => (job.departmentBudgets?.[department]?.[monthKey] || 0) > 0);
    return departmentsWithAllocations.length > 0 && departmentsWithAllocations.every(department => isDepartmentMonthValidated(job, department, monthKey));
  };
  const hasValidatedAllocation = (job: Job) => job.departments.some(department => months.some(month => isDepartmentMonthValidated(job, department, month.key)));
  const visibleJobs = showValidatedOnly ? jobs.filter(hasValidatedAllocation) : jobs;
  const getJobMonthTotal = (job: Job, monthKey: string) => job.departments.reduce((sum, department) => {
    const amount = job.departmentBudgets?.[department]?.[monthKey] || 0;
    return sum + (showValidatedOnly && !isDepartmentMonthValidated(job, department, monthKey) ? 0 : amount);
  }, 0);
  const getMonthTotal = (monthKey: string) => visibleJobs.reduce((sum, job) => sum + getJobMonthTotal(job, monthKey), 0);
  const monthlyTotals = months.map(month => getMonthTotal(month.key));
  const visibleTotal = monthlyTotals.reduce((sum, amount) => sum + amount, 0);
  const toggleJob = (jobId: string) => {
    setExpandedJobs(current => {
      const next = new Set(current);
      next.has(jobId) ? next.delete(jobId) : next.add(jobId);
      return next;
    });
  };
  const exportAllocations = () => {
    type AllocationRow = {
      job: Job;
      department: string;
      allocated: number;
    };
    const rows: AllocationRow[] = visibleJobs.flatMap(job => job.departments.filter(department => !showValidatedOnly || months.some(month => isDepartmentMonthValidated(job, department, month.key))).map(department => ({
      job,
      department,
      allocated: showValidatedOnly ? months.reduce((sum, month) => sum + (isDepartmentMonthValidated(job, department, month.key) ? job.departmentBudgets?.[department]?.[month.key] || 0 : 0), 0) : getDepartmentTotal(job, department)
    })));
    downloadCsv<AllocationRow>(`monthly-budget-allocations-${months[0].key}.csv`, [{
      label: 'Job ID',
      value: row => row.job.ref
    }, {
      label: 'Job title',
      value: row => row.job.name
    }, {
      label: 'Client',
      value: row => row.job.client
    }, {
      label: 'Status',
      value: row => STATUS_CONFIG[row.job.status].label
    }, {
      label: 'Project director',
      value: row => row.job.projectDirector
    }, {
      label: 'Project manager',
      value: row => row.job.projectManager
    }, {
      label: 'Department',
      value: row => row.department
    }, {
      label: 'Job value',
      value: row => row.job.value
    }, {
      label: 'Allocated',
      value: row => row.allocated
    }, ...months.flatMap(month => [{
      label: month.label,
      value: (row: AllocationRow) => showValidatedOnly && !isDepartmentMonthValidated(row.job, row.department, month.key) ? 0 : row.job.departmentBudgets?.[row.department]?.[month.key] || 0
    }, {
      label: `${month.label} validated`,
      value: (row: AllocationRow) => isDepartmentMonthValidated(row.job, row.department, month.key) ? 'Yes' : 'No'
    }])], rows);
  };
  return <div className="flex-1 flex flex-col min-h-0 p-5 md:p-8 gap-4" data-id="element-1907" data-mp="vgdfwi060u5n">
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-1908" data-mp="7rkj6jbpyi9z">
        <div data-id="element-1909" data-mp="7phhh217f0pf">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-1910" data-mp="2sr6gt49avav">
            Monthly budget allocations
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-1911" data-mp="2efulluxif7xx">
            Allocate budget at department level; project totals roll up
            automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-1912" data-mp="25foaugt74m2g">
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1" aria-label="Budget month navigation" data-id="element-1913" data-mp="25h9505blm2zs">
            <button type="button" onClick={() => setMonthOffset(value => value - 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Show earlier months" data-id="element-1914" data-mp="2x6u5ihq5drkc">
              <ChevronLeft className="w-4 h-4" data-id="element-1915" data-mp="323b1fy3l9a7z" />
            </button>
            <button type="button" onClick={() => setMonthOffset(0)} className="px-2 text-xs font-medium text-[#14466B] hover:underline" data-id="element-1916" data-mp="2o5e8rcs99nyl">
              Current month
            </button>
            <button type="button" onClick={() => setMonthOffset(value => value + 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Show later months" data-id="element-1917" data-mp="1cpxxwb3avq7x">
              <ChevronRight className="w-4 h-4" data-id="element-1918" data-mp="1ryy114ignimh" />
            </button>
          </div>
          <button type="button" onClick={exportAllocations} disabled={visibleJobs.length === 0} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-400" title="Export the allocations shown as CSV" data-id="element-1919" data-mp="1wyu7daemrm57">
            <Download className="h-4 w-4" data-id="element-1920" data-mp="1e0zpe6viy0ao" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl bg-white shadow-sm" data-id="element-1921" data-mp="zcdcscxa7mn5">
        <table className="w-full min-w-[880px] text-left border-collapse" data-id="element-1922" data-mp="147iypbqop1b8">
          <thead className="sticky top-0 z-10 bg-slate-50" data-id="element-1923" data-mp="qadw5lq6ikkq">
            <tr className="border-b border-slate-200" data-id="element-1924" data-mp="ubz20n4apylf">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[230px] min-w-[230px]" data-id="element-1925" data-mp="udjy4h69dple">
                Project / department
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[130px]" data-id="element-1926" data-mp="6pn50akfqzr9">
                Status
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[80px]" data-id="element-1927" data-mp="fo8t0l8of6zf">
                PD
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[80px]" data-id="element-1928" data-mp="ujwu41ip7dz3">
                PM
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right" data-id="element-1929" data-mp="13el0jv00f1ph">
                Job value
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right" data-id="element-1930" data-mp="zf3t4w10vuho">
                Allocated
              </th>
              {months.map(month => <th key={month.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right min-w-[130px]" data-id="element-1931" data-mp="18f8zd2uhcmu9">
                  {month.label}
                </th>)}
            </tr>
            <tr className="bg-slate-100/70 border-b border-slate-200" data-id="element-1932" data-mp="9z8t7zitsa64">
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600" data-id="element-1933" data-mp="ez50q8qqejqf">
                Monthly total
              </th>
              <th className="px-4 py-2.5" aria-label="Status total" data-id="element-1934" data-mp="or0xngh303t2" />
              <th className="px-4 py-2.5" aria-label="Project director total" data-id="element-1935" data-mp="osu5v07cew36" />
              <th className="px-4 py-2.5" aria-label="Project manager total" data-id="element-1936" data-mp="tnrdc2oz4xdy" />
              <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 text-right tabular-nums" data-id="element-1937" data-mp="xsh9ztrgifzx">
                {formatCurrency(visibleJobs.reduce((sum, job) => sum + job.value, 0))}
              </th>
              <th className="px-4 py-2.5 text-xs font-bold text-[#14466B] text-right tabular-nums" data-id="element-1938" data-mp="2zk3r93ljqwj7">
                {formatCurrency(visibleTotal)}
              </th>
              {monthlyTotals.map((total, index) => <th key={months[index].key} className="px-4 py-2.5 text-xs font-semibold text-slate-700 text-right tabular-nums" data-id="element-1939" data-mp="2wzp9n1szjwqe" data-mpi={index}>
                  {total ? formatCurrency(total) : <span className="font-normal text-slate-300" data-id="element-1940" data-mp="2n7jl621xzxfy">—</span>}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1941" data-mp="2iajh1wonl4rh">
            {visibleJobs.map(job => {
            const expanded = expandedJobs.has(job.id);
            const total = showValidatedOnly ? months.reduce((sum, month) => sum + getJobMonthTotal(job, month.key), 0) : getJobTotal(job);
            const overAllocated = job.value > 0 && total > job.value;
            return <Fragment key={job.id} data-id="element-1942" data-mp="31xqv1tdvw851">
                  <tr className="bg-white hover:bg-slate-50" data-id="element-1943" data-mp="31wo1fdp6frlf">
                    <td className="px-4 py-3" data-id="element-1944" data-mp="1ujw4gy3slqyl">
                      <div className="flex items-center gap-2" data-id="element-1945" data-mp="1uhjcds4bm07t">
                        <button type="button" onClick={() => toggleJob(job.id)} aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${job.name} allocations`} className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-1946" data-mp="1qgzdok43mojk">
                          <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-1947" data-mp="34h2koc0o9ja0" />
                        </button>
                        <button type="button" onClick={() => onOpenJob(job)} className="min-w-0 text-left group" data-id="element-1948" data-mp="39fx2bamwl1sj">
                          <span className="block text-sm font-medium text-slate-900 transition-colors group-hover:text-[#14466B] group-hover:underline group-focus-visible:text-[#14466B] group-focus-visible:underline" data-id="element-1949" data-mp="y0yijit0bag">
                            {job.name}
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5" data-id="element-1950" data-mp="3s882uxu34nnh">
                            {job.ref} · {job.departments.length} department
                            {job.departments.length === 1 ? '' : 's'}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-1951" data-mp="248vvqnfca1vw">
                      <StatusBadge status={job.status} data-id="element-1952" data-mp="2484bu9v3tslg" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1953" data-mp="2j4bwfbafcw19">
                      <PersonAvatar name={job.projectDirector} roleLabel="Project director" tone="director" data-id="element-1954" data-mp="2e6k0m66nsprs" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1955" data-mp="1qi0u9w766n2c">
                      <PersonAvatar name={job.projectManager} roleLabel="Project manager" tone="manager" data-id="element-1956" data-mp="1qemr4lqom9tu" />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right tabular-nums" data-id="element-1957" data-mp="207anjfdr2bs1">
                      {formatCurrency(job.value)}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold text-right tabular-nums ${overAllocated ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1958" data-mp="17qhs1rkyzd1d">
                      {total ? formatCurrency(total) : <span className="font-normal text-slate-300" data-id="element-1959" data-mp="12pkc7lnfmv31">—</span>}
                    </td>
                    {months.map(month => {
                  const monthTotal = getJobMonthTotal(job, month.key);
                  const isValidated = isJobMonthValidated(job, month.key);
                  return <td key={month.key} className={`px-4 py-3 text-sm text-right tabular-nums ${isValidated ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600'}`} data-id="element-1960" data-mp="219xc3wl2fy5t">
                          {monthTotal ? formatCurrency(monthTotal) : <span className="text-slate-300" data-id="element-1961" data-mp="1dhzzw4dpf446">—</span>}
                        </td>;
                })}
                  </tr>
                  {expanded && job.departments.filter(department => !showValidatedOnly || months.some(month => isDepartmentMonthValidated(job, department, month.key))).map(department => <tr key={`${job.id}-${department}`} className="bg-slate-50/70" data-id="element-1962" data-mp="2twrsahauobdh">
                        <td className="px-4 py-2.5 pl-11 text-sm text-slate-700" data-id="element-1963" data-mp="2yvmb3j8thzxl">
                          <span className="font-medium underline decoration-slate-300 underline-offset-4" data-id="element-1964" data-mp="2yxfkh6atey9a">
                            {department}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1965" data-mp="dig2yh8g09c7">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1966" data-mp="8ibmusbg5gmi">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1967" data-mp="4fex8hnhok92">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs text-slate-400" data-id="element-1968" data-mp="3qo6gx50i8s48">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-700 text-right tabular-nums" data-id="element-1969" data-mp="3lrzj5zi9imxd">
                          {showValidatedOnly ? (() => {
                    const total = months.reduce((sum, month) => sum + (isDepartmentMonthValidated(job, department, month.key) ? job.departmentBudgets?.[department]?.[month.key] || 0 : 0), 0);
                    return total ? formatCurrency(total) : <span className="text-slate-300" data-id="element-1970" data-mp="xsineyh0901a">—</span>;
                  })() : getDepartmentTotal(job, department) ? formatCurrency(getDepartmentTotal(job, department)) : <span className="text-slate-300" data-id="element-1971" data-mp="jyrm2ddfgmgn">—</span>}
                        </td>
                        {months.map(month => <td key={month.key} className="p-0" data-id="element-1972" data-mp="a2o8mbd4l1ee">
                            {!showValidatedOnly || isDepartmentMonthValidated(job, department, month.key) ? <BudgetCell value={job.departmentBudgets?.[department]?.[month.key] || 0} isValidated={Boolean(job.validatedDepartmentBudgets?.[department]?.[month.key])} onChange={amount => onUpdateBudget(job.id, department, month.key, amount)} onValidate={() => onValidateBudget(job.id, department, month.key)} onUnvalidate={() => onUnvalidateBudget(job.id, department, month.key)} data-id="element-1973" data-mp="f2sp41e19w4m" /> : <span className="flex min-h-[48px] items-center justify-end px-4 text-sm text-slate-300" data-id="element-1974" data-mp="1zhcnqkd3oxg4">—</span>}
                          </td>)}
                      </tr>)}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}
function BudgetCell({
  value,
  isValidated,
  onChange,
  onValidate,
  onUnvalidate
}: {
  value: number;
  isValidated: boolean;
  onChange: (val: number) => void;
  onValidate: () => void;
  onUnvalidate: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isEditing) setTempValue(value ? value.toString() : '');
  }, [value, isEditing]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  const commit = (validate = false) => {
    const amount = Number(tempValue);
    if (tempValue.trim() === '' || Number.isNaN(amount) || amount < 0) {
      setTempValue(value ? value.toString() : '');
      setIsEditing(false);
      return;
    }
    if (amount !== value) onChange(amount);
    if (validate) onValidate();
    setIsEditing(false);
  };
  if (isEditing) {
    return <div className="flex min-h-[48px] bg-blue-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-400" data-id="element-1975" data-mp="1ewtu302g7hb0">
        <input ref={inputRef} type="number" min="0" step="0.01" value={tempValue} onChange={e => setTempValue(e.target.value)} onBlur={() => commit()} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          setTempValue(value ? value.toString() : '');
          setIsEditing(false);
        }
      }} className="min-w-0 flex-1 bg-transparent px-2 py-3 text-right text-sm text-slate-900 outline-none" placeholder="0" aria-label="Edit monthly sales allocation" data-id="element-1976" data-mp="19xpmuez33tnb" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => commit(true)} className="m-1 inline-flex w-8 flex-shrink-0 items-center justify-center rounded-md bg-emerald-600 text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200" aria-label="Validate monthly sales allocation" title="Validate allocation" data-id="element-1977" data-mp="2qbpj9fyq115t"><CheckCircle2 className="h-4 w-4" data-id="element-1978" data-mp="32n04iu0hgokd" /></button>
      </div>;
  }
  return <div className={`flex min-h-[48px] w-full items-center justify-end gap-1 px-1 text-right text-sm transition-colors ${isValidated ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1979" data-mp="32p30duobhbx0">
      <button type="button" onClick={() => setIsEditing(true)} className="flex min-h-[48px] min-w-0 flex-1 items-center justify-end gap-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400" aria-label={`${isValidated ? 'Edit validated' : 'Edit'} monthly sales allocation`} title={isValidated ? 'Validated allocation — click to edit' : 'Click to edit'} data-id="element-1980" data-mp="2exfdroy38n0j">
        {isValidated && <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-1981" data-mp="45cjadxgdkz7" />}
        {value > 0 ? formatCurrency(value) : <span className="text-slate-300" data-id="element-1982" data-mp="207zid810u76l">—</span>}
      </button>
      {isValidated && <button type="button" onClick={onUnvalidate} className="mr-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-100" aria-label="Unvalidate monthly sales allocation" title="Unvalidate allocation" data-id="element-1983" data-mp="1v9ehd6dy3jh3"><X className="h-4 w-4" data-id="element-1984" data-mp="1hj2np31ppcnu" /></button>}
    </div>;
}